import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  ChevronRight,
  Compass,
  Copy,
  Cpu,
  Database,
  History,
  LayoutGrid,
  Maximize2,
  MessageCirclePlus,
  MessageSquareText,
  Mic,
  RefreshCw,
  Rows,
  Search,
  Send,
  Settings,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import { calculatePanApi, chatApiStream, getFeatureChatConfig, getStoredUser, StoredUser, submitClientLog } from '../lib/api';
import { loadPanRecords, savePanRecords, toDateTimeLocalValue } from './pan/pan-persistence';
import type { SavedPanRecord } from './pan/pan-types';

const PRESET_VISIBLE_COUNT = 8;
const FOLLOWUP_HISTORY_TURN_LIMIT = 6;
const PAN_GRID_POSITIONS = [4, 9, 2, 3, 5, 7, 8, 1, 6];
const PAN_OUTER_POSITIONS: Array<{ position: number; className: string; side: 'top' | 'left' | 'right' | 'bottom' }> = [
  { position: 9, className: 'wence-pan-outer-pos9', side: 'top' },
  { position: 4, className: 'wence-pan-outer-pos4', side: 'left' },
  { position: 3, className: 'wence-pan-outer-pos3', side: 'left' },
  { position: 8, className: 'wence-pan-outer-pos8', side: 'left' },
  { position: 2, className: 'wence-pan-outer-pos2', side: 'right' },
  { position: 7, className: 'wence-pan-outer-pos7', side: 'right' },
  { position: 6, className: 'wence-pan-outer-pos6', side: 'right' },
  { position: 1, className: 'wence-pan-outer-pos1', side: 'bottom' },
];
const PAN_HORSE_CORNER_CLASS_MAP: Record<number, string> = {
  4: 'wence-pan-horse-corner4',
  2: 'wence-pan-horse-corner2',
  8: 'wence-pan-horse-corner8',
  6: 'wence-pan-horse-corner6',
};
const STEM_SET = new Set(['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸']);
const PAN_CTA_HINT_PATTERNS = [
  /可以起局/,
  /开始起局/,
  /先起局/,
  /先排盘/,
  /排盘看/,
  /排个局/,
  /排一局/,
  /起局看/,
  /起盘/,
  /点击.*起局/,
  /去起局/,
  /诚心想着.*起局/,
  /起个局/,
];

const BANNED_DISPLAY_PATTERNS = [
  /经验推断/g,
  /待核验/g,
  /你这开门见山的劲儿/g,
  /局就在这儿，跑不了/g,
];

const DEFAULT_BRACKET_GUIDANCE_KEYWORDS = '轩辕老师,老师,道长,贫道,老道,老夫,在下,放下茶杯,端起茶杯,端起茶盏,放下茶盏,抿茶,抿一口茶,轻抿,掐指,掐指一算,捻须,抚须,拈须,沉吟,轻叹,摇头,点头,笑了笑,长辈口吻,口吻,语气,追问,引导,提示词,措辞,表达方式,输出,回复,分步,步骤,第一步,第二步,第三步,先以,先用,先从,先问,先确认,请先,再问,再补';
const DAO_AVATAR_SRC = '/dao-avatar.png';

const escapeRegexLiteral = (text: string): string => String(text || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildBracketGuidanceKeywordRegex = (rawKeywords: string): RegExp => {
  const tokens = Array.from(new Set([
    ...DEFAULT_BRACKET_GUIDANCE_KEYWORDS.split(/[\n,，;；、|]+/g).map((item) => item.trim()).filter(Boolean),
    ...String(rawKeywords || '').split(/[\n,，;；、|]+/g).map((item) => item.trim()).filter(Boolean),
  ])).slice(0, 80);
  const source = tokens.map((item) => escapeRegexLiteral(item)).join('|');
  if (!source) return /(长辈口吻|温和追问)/u;
  try {
    return new RegExp(`(${source})`, 'u');
  } catch {
    return /(长辈口吻|温和追问)/u;
  }
};

const stripGuidanceParentheticals = (raw: string, keywordRegex: RegExp, enabled: boolean): string => {
  if (!enabled) return String(raw || '');
  return String(raw || '')
    .replace(/[（(]([^（）()]{1,42})[）)]/g, (full, inner) => {
      const text = String(inner || '').trim();
      if (!text) return full;
      return keywordRegex.test(text) ? '' : full;
    })
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n');
};

const WENCE_ROLEPLAY_STAGE_DIRECTION_REGEX = /(^|[\n。！？!?]\s*)[（(][^（）()]{0,40}(?:看着盘面|仔细看着盘面|凝视盘面|看盘|端详盘面|低头看盘|眉头微皱|轻轻皱眉|皱了皱眉|皱眉|沉吟片刻|沉吟|思索片刻|思索|掐指一算|掐指|轻叹|笑了笑|顿了顿|停了一下)[^（）()]{0,40}[）)]\s*/gu;

const applyBootstrapLaymanPolish = (raw: string, mode: string): string => {
  const normalizedMode = String(mode || '').trim().toLowerCase();
  let text = String(raw || '').replace(WENCE_ROLEPLAY_STAGE_DIRECTION_REGEX, '$1');
  if (normalizedMode !== 'post_pan_bootstrap') return text;
  return text
    .replace(/^\s*局起了[。！!？?]?\s*/u, '')
    .replace(/^\s*(?:好[，,]\s*)?我们先看盘[。！!？?]?\s*/u, '先说结论：')
    .replace(/^\s*好的[，,]\s*我们一起看看这个局[。！!？?]?\s*/u, '先说结论：')
    .replace(/^\s*好[，,]\s*盘面出来了[。！!？?]?\s*/u, '先说结论：')
    .replace(/^\s*好[，,]\s*盘面我看完了[。！!？?]?\s*/u, '先说结论：')
    .replace(/^\s*好[，,]\s*我直接按盘面来回答你的问题[。！!？?]?\s*/u, '先说结论：')
    .replace(/我先说最核心的判断[：:]/gu, '先说结论：')
    .replace(/我先说最核心的结论[：:]/gu, '先说结论：')
    .replace(/我先说最核心的一点[：:]/gu, '先说重点：')
    .replace(/我先说你[。！!？?]/gu, '先说你的情况。')
    .replace(/理由很简单[，,]/gu, '原因是，')
    .replace(/盘面依据[：:]/gu, '原因是：')
    .replace(/这结论从盘面看得很清楚[：:]/gu, '原因是：');
};

const SUGGESTION_POOL = [
  '最近这份工作还要不要继续？',
  '这段感情还有没有复合机会？',
  '今年换城市发展合适吗？',
  '现在创业是不是好时机？',
  'Ta旺我吗？',
  '我们适合结婚吗？',
  '旅行去哪个城市好？',
  '今年哪个方位对我有利？',
  '我现在是该守还是该冲？',
  '这次谈判我能拿下吗？',
  '最近一直失眠，是哪块气场有问题？',
  '这笔投资该不该做？',
  '要不要尽快跳槽？',
  '要不要和这个人继续合作？',
  '我和父母关系怎么缓和？',
  '今年财运重点在哪个月？',
  '这个项目为什么总卡壳？',
  '我最近总心慌，应该先做什么调整？',
  '该不该主动联系那个贵人？',
  '现在买房还是再等一等？',
];

type MessageStatus = 'normal' | 'streaming';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  status?: MessageStatus;
  /** 起局后用于在聊天气泡中渲染九宫格 */
  panRecordId?: string;
}

interface WenceAppProps {
  onBack: () => void;
  onOpenPan?: (seedQuestion?: string) => void;
  appLabel?: string;
  daoName?: string;
  featureId?: string;
  storageNamespace?: string;
}

interface PersistedMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  status?: MessageStatus;
  panRecordId?: string;
}

interface PersistedWenceState {
  messages: PersistedMessage[];
  inputValue: string;
  isFirstMessage: boolean;
  suggestions: string[];
  usedPanRecordId: string | null;
  bootstrappedPanRecordId: string | null;
  panSummary: string;
  panCtaReady?: boolean;
  latestDebug?: WenceDebugInfo | null;
  debugHistory?: WenceDebugHistoryItem[];
  autoSyncPanContext?: boolean;
}

interface PersistedWenceHistoryItem {
  id: string;
  title: string;
  updatedAt: string;
  createdAt: string;
  state: PersistedWenceState;
}

interface WenceTokenUsage {
  promptTokens: number | null;
  completionTokens: number | null;
  reasoningTokens: number | null;
  totalTokens: number | null;
}

interface WenceDebugInfo {
  modelText: string;
  featurePromptPreview: string;
  thinkingSystemPromptPreview: string;
  thinkingUserPromptPreview: string;
  chainPromptPreview: string;
  chainPromptChars: number | null;
  chainPromptSectionCount: number | null;
  promptMode?: string;
  thinkingResult: Record<string, unknown> | null;
  twoStepThinkingEnabled?: boolean;
  twoStepThinkingSuccess?: boolean;
  twoStepThinkingFallbackUsed?: boolean;
  twoStepThinkingFallbackReason?: string;
  twoStepThinkingError?: string;
  knowledgeRefs: Array<{ title?: string; source?: string; reason?: string }>;
  knowledgeQueryUsed: string;
  durationMs: number | null;
  promptPolicy: string;
  promptPolicyReason: string;
  featurePromptChars: number | null;
  finishReason: string;
  tokenUsage: WenceTokenUsage;
  sanitizeConfig: {
    bracketSanitizeEnabled: boolean;
    bracketGuidanceKeywords: string;
    dualLayer?: boolean;
    source?: string;
  } | null;
  outputConfig: Record<string, unknown> | null;
}

interface WenceUiSanitizeConfig {
  bracketSanitizeEnabled: boolean;
  bracketGuidanceKeywords: string;
  dualLayer?: boolean;
  source?: string;
}

interface WenceDebugHistoryItem {
  id: string;
  ts: string;
  mode: string;
  userText: string;
  assistantPreview: string;
  durationMs: number | null;
  knowledgeRefCount: number;
  promptPolicy: string;
  finishReason: string;
  tokenUsage: WenceTokenUsage;
}

const buildWenceStateStorageKey = (namespace: string): string => `qimen_${namespace}_state_v1`;
const buildWenceHistoryStorageKey = (namespace: string): string => `qimen_${namespace}_history_v1`;

const readPersistedWenceStateRaw = (storageKey: string): string => {
  if (typeof window === 'undefined') return '';
  try {
    const localRaw = localStorage.getItem(storageKey) || '';
    if (localRaw) return localRaw;
  } catch {
    // noop
  }
  try {
    const sessionRaw = sessionStorage.getItem(storageKey) || '';
    if (sessionRaw) {
      try {
        localStorage.setItem(storageKey, sessionRaw);
      } catch {
        // noop
      }
      return sessionRaw;
    }
  } catch {
    // noop
  }
  return '';
};

const persistWenceState = (storageKey: string, payload: PersistedWenceState): void => {
  const serialized = JSON.stringify(payload);
  try {
    localStorage.setItem(storageKey, serialized);
  } catch {
    // noop
  }
  try {
    sessionStorage.setItem(storageKey, serialized);
  } catch {
    // noop
  }
};

const clearPersistedWenceState = (storageKey: string): void => {
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // noop
  }
  try {
    sessionStorage.removeItem(storageKey);
  } catch {
    // noop
  }
};

const pickSuggestions = (previous: string[] = []): string[] => {
  const pool = [...SUGGESTION_POOL];
  const picked: string[] = [];
  while (pool.length > 0 && picked.length < PRESET_VISIBLE_COUNT) {
    const idx = Math.floor(Math.random() * pool.length);
    const [next] = pool.splice(idx, 1);
    if (next) picked.push(next);
  }
  if (picked.join('|') === previous.join('|')) {
    return [...picked.slice(1), picked[0]].filter(Boolean);
  }
  return picked;
};

const loadPersistedState = (storageKey: string): PersistedWenceState | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = readPersistedWenceStateRaw(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedWenceState> | null;
    if (!parsed || !Array.isArray(parsed.messages)) return null;
    return {
      messages: parsed.messages,
      inputValue: typeof parsed.inputValue === 'string' ? parsed.inputValue : '',
      isFirstMessage: parsed.isFirstMessage !== false,
      suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions.filter((item) => typeof item === 'string') : [],
      usedPanRecordId: typeof parsed.usedPanRecordId === 'string' && parsed.usedPanRecordId.trim() ? parsed.usedPanRecordId : null,
      bootstrappedPanRecordId: typeof parsed.bootstrappedPanRecordId === 'string' && parsed.bootstrappedPanRecordId.trim() ? parsed.bootstrappedPanRecordId : null,
      panSummary: typeof parsed.panSummary === 'string' ? parsed.panSummary : '',
      panCtaReady: parsed.panCtaReady === true,
      latestDebug: parsed.latestDebug && typeof parsed.latestDebug === 'object'
        ? {
          modelText: String(parsed.latestDebug.modelText || ''),
          featurePromptPreview: String(parsed.latestDebug.featurePromptPreview || ''),
          thinkingSystemPromptPreview: String(parsed.latestDebug.thinkingSystemPromptPreview || ''),
          thinkingUserPromptPreview: String(parsed.latestDebug.thinkingUserPromptPreview || ''),
          chainPromptPreview: String(parsed.latestDebug.chainPromptPreview || ''),
          chainPromptChars: Number.isFinite(Number(parsed.latestDebug.chainPromptChars))
            ? Number(parsed.latestDebug.chainPromptChars)
            : null,
          chainPromptSectionCount: Number.isFinite(Number(parsed.latestDebug.chainPromptSectionCount))
            ? Number(parsed.latestDebug.chainPromptSectionCount)
            : null,
          promptMode: String(parsed.latestDebug.promptMode || ''),
          thinkingResult: parsed.latestDebug.thinkingResult && typeof parsed.latestDebug.thinkingResult === 'object'
            ? parsed.latestDebug.thinkingResult as Record<string, unknown>
            : null,
          twoStepThinkingEnabled: parsed.latestDebug.twoStepThinkingEnabled === true,
          twoStepThinkingSuccess: parsed.latestDebug.twoStepThinkingSuccess === true,
          twoStepThinkingFallbackUsed: parsed.latestDebug.twoStepThinkingFallbackUsed === true,
          twoStepThinkingFallbackReason: String(parsed.latestDebug.twoStepThinkingFallbackReason || ''),
          twoStepThinkingError: String(parsed.latestDebug.twoStepThinkingError || ''),
          knowledgeRefs: Array.isArray(parsed.latestDebug.knowledgeRefs)
            ? parsed.latestDebug.knowledgeRefs
              .filter((item): item is { title?: string; source?: string; reason?: string } => Boolean(item && typeof item === 'object'))
              .map((item) => ({
                title: typeof item.title === 'string' ? item.title : '',
                source: typeof item.source === 'string' ? item.source : '',
                reason: typeof item.reason === 'string' ? item.reason : '',
              }))
            : [],
          knowledgeQueryUsed: String(parsed.latestDebug.knowledgeQueryUsed || ''),
          durationMs: Number.isFinite(Number(parsed.latestDebug.durationMs)) ? Number(parsed.latestDebug.durationMs) : null,
          promptPolicy: String(parsed.latestDebug.promptPolicy || ''),
          promptPolicyReason: String(parsed.latestDebug.promptPolicyReason || ''),
          featurePromptChars: Number.isFinite(Number(parsed.latestDebug.featurePromptChars))
            ? Number(parsed.latestDebug.featurePromptChars)
            : null,
          finishReason: String(parsed.latestDebug.finishReason || ''),
          tokenUsage: parsed.latestDebug.tokenUsage && typeof parsed.latestDebug.tokenUsage === 'object'
            ? {
              promptTokens: Number.isFinite(Number(parsed.latestDebug.tokenUsage.promptTokens))
                ? Number(parsed.latestDebug.tokenUsage.promptTokens)
                : null,
              completionTokens: Number.isFinite(Number(parsed.latestDebug.tokenUsage.completionTokens))
                ? Number(parsed.latestDebug.tokenUsage.completionTokens)
                : null,
              reasoningTokens: Number.isFinite(Number(parsed.latestDebug.tokenUsage.reasoningTokens))
                ? Number(parsed.latestDebug.tokenUsage.reasoningTokens)
                : null,
              totalTokens: Number.isFinite(Number(parsed.latestDebug.tokenUsage.totalTokens))
                ? Number(parsed.latestDebug.tokenUsage.totalTokens)
                : null,
            }
            : {
              promptTokens: null,
              completionTokens: null,
              reasoningTokens: null,
              totalTokens: null,
            },
          sanitizeConfig: parsed.latestDebug.sanitizeConfig && typeof parsed.latestDebug.sanitizeConfig === 'object'
            ? {
              bracketSanitizeEnabled: (parsed.latestDebug.sanitizeConfig as Record<string, unknown>).bracketSanitizeEnabled !== false,
              bracketGuidanceKeywords: String((parsed.latestDebug.sanitizeConfig as Record<string, unknown>).bracketGuidanceKeywords || ''),
              dualLayer: (parsed.latestDebug.sanitizeConfig as Record<string, unknown>).dualLayer === true,
              source: String((parsed.latestDebug.sanitizeConfig as Record<string, unknown>).source || ''),
            }
            : null,
          outputConfig: parsed.latestDebug.outputConfig && typeof parsed.latestDebug.outputConfig === 'object'
            ? parsed.latestDebug.outputConfig as Record<string, unknown>
            : null,
        }
        : null,
      debugHistory: Array.isArray(parsed.debugHistory)
        ? parsed.debugHistory
          .filter((item): item is WenceDebugHistoryItem => Boolean(item && typeof item === 'object'))
          .map((item) => ({
            id: String(item.id || `dbg-${Date.now()}`),
            ts: String(item.ts || new Date().toISOString()),
            mode: String(item.mode || ''),
            userText: String(item.userText || ''),
            assistantPreview: String(item.assistantPreview || ''),
            durationMs: Number.isFinite(Number(item.durationMs)) ? Number(item.durationMs) : null,
            knowledgeRefCount: Number.isFinite(Number(item.knowledgeRefCount)) ? Number(item.knowledgeRefCount) : 0,
            promptPolicy: String(item.promptPolicy || ''),
            finishReason: String(item.finishReason || ''),
            tokenUsage: item.tokenUsage && typeof item.tokenUsage === 'object'
              ? {
                promptTokens: Number.isFinite(Number(item.tokenUsage.promptTokens)) ? Number(item.tokenUsage.promptTokens) : null,
                completionTokens: Number.isFinite(Number(item.tokenUsage.completionTokens)) ? Number(item.tokenUsage.completionTokens) : null,
                reasoningTokens: Number.isFinite(Number(item.tokenUsage.reasoningTokens)) ? Number(item.tokenUsage.reasoningTokens) : null,
                totalTokens: Number.isFinite(Number(item.tokenUsage.totalTokens)) ? Number(item.tokenUsage.totalTokens) : null,
              }
              : {
                promptTokens: null,
                completionTokens: null,
                reasoningTokens: null,
                totalTokens: null,
              },
          }))
        : [],
      autoSyncPanContext: parsed.autoSyncPanContext !== false,
    };
  } catch {
    return null;
  }
};

const deserializeMessages = (items: PersistedMessage[]): Message[] => (
  items
    .filter((item) => item && (item.role === 'user' || item.role === 'assistant'))
    .map((item) => ({
      id: String(item.id || `msg-${Date.now()}`),
      role: item.role,
      content: String(item.content || ''),
      timestamp: new Date(item.timestamp || Date.now()),
      status: item.status === 'streaming' ? 'streaming' : 'normal',
      panRecordId: typeof item.panRecordId === 'string' && item.panRecordId ? item.panRecordId : undefined,
    }))
);

const serializeMessages = (items: Message[]): PersistedMessage[] => (
  items.map((item) => ({
    id: item.id,
    role: item.role,
    content: item.content,
    timestamp: item.timestamp.toISOString(),
    status: item.status === 'streaming' ? 'streaming' : 'normal',
    panRecordId: item.panRecordId,
  }))
);

const loadPersistedWenceHistory = (storageKey: string): PersistedWenceHistoryItem[] => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item === 'object')
      .map((item) => {
        const state = item.state && typeof item.state === 'object' ? item.state as PersistedWenceState : null;
        if (!state || !Array.isArray(state.messages)) return null;
        return {
          id: String(item.id || `hist-${Date.now()}`),
          title: String(item.title || '未命名会话').trim() || '未命名会话',
          updatedAt: String(item.updatedAt || new Date().toISOString()),
          createdAt: String(item.createdAt || item.updatedAt || new Date().toISOString()),
          state,
        } as PersistedWenceHistoryItem;
      })
      .filter((item): item is PersistedWenceHistoryItem => Boolean(item))
      .slice(0, 40);
  } catch {
    return [];
  }
};

const savePersistedWenceHistory = (storageKey: string, items: PersistedWenceHistoryItem[]) => {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(storageKey, JSON.stringify((Array.isArray(items) ? items : []).slice(0, 40)));
  } catch {
    // noop
  }
};

const getLatestPanRecord = (): SavedPanRecord | null => {
  const records = loadPanRecords();
  if (!records.length) return null;
  const sorted = [...records].sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
  return sorted[0] || null;
};

const buildPanSummary = (record: SavedPanRecord): string => {
  const pan = record.pan;
  const question = String(record.question || pan.question || '').trim() || '未填写事项';
  const timeText = String(pan.timeNodeText || record.sourceDateTime || '').replace('T', ' ') || '-';
  const juText = `${record.panMode === 'ke' ? '刻盘' : '时盘'} · ${pan.juText || '-'}`;
  const core = [
    `事项：${question}`,
    `时间：${timeText}`,
    `盘型：${juText}`,
    `旬首：${pan.xunShou || '-'}`,
    `值符：${pan.zhiFu || '-'}`,
    `值使：${pan.zhiShi || '-'}`,
    `空亡：${pan.kongText || '-'}`,
    `马星：${pan.maText || '-'}`,
  ];
  return core.join('；');
};

const buildPanContextForApi = (record: SavedPanRecord): string => {
  const pan = record.pan;
  const panPayload = {
    question: String(record.question || pan.question || '').trim() || '未填写事项',
    mode: record.panMode === 'ke' ? 'ke' : 'hour',
    panMode: record.panMode === 'ke' ? 'ke' : 'hour',
    timeNodeText: String(pan.timeNodeText || record.sourceDateTime || ''),
    dateText: String(pan.dateText || ''),
    solarTermText: String(pan.solarTermText || ''),
    juText: String(pan.juText || ''),
    xunShou: String(pan.xunShou || ''),
    zhiFu: String(pan.zhiFu || ''),
    zhiShi: String(pan.zhiShi || ''),
    kongText: String(pan.kongText || ''),
    maText: String(pan.maText || ''),
    horsePosition: Number(pan.horsePosition || 0),
    yearPillar: String(pan.yearPillar || ''),
    monthPillar: String(pan.monthPillar || ''),
    dayPillar: String(pan.dayPillar || ''),
    hourPillar: String(pan.hourPillar || ''),
    kePillar: String(pan.kePillar || ''),
    consultMode: record.consultMode === 'face' ? 'face' : 'remote',
    palaces: Array.isArray(pan.palaces) ? pan.palaces : [],
  };
  return [
    '【本局盘面摘要】',
    `事项：${String(record.question || pan.question || '').trim() || '未填写事项'}`,
    `时间：${String(pan.timeNodeText || record.sourceDateTime || '').replace('T', ' ') || '-'}`,
    `盘型：${record.panMode === 'ke' ? '刻盘' : '时盘'} ${pan.juText || '-'}`,
    `旬首：${pan.xunShou || '-'}；值符：${pan.zhiFu || '-'}；值使：${pan.zhiShi || '-'}；空亡：${pan.kongText || '-'}；马星：${pan.maText || '-'}`,
    `四柱：年${pan.yearPillar || '-'} 月${pan.monthPillar || '-'} 日${pan.dayPillar || '-'} 时${pan.hourPillar || '-'}${record.panMode === 'ke' ? ` 刻${pan.kePillar || '-'}` : ''}`,
    '【本局盘面数据】',
    JSON.stringify(panPayload),
  ].join('\n');
};

const isPanBridgeMessage = (item: Message): boolean => (
  item.id.startsWith('assistant-pan-preview-') || item.id.startsWith('pan-bridge-')
);

const resolvePanRecordFromHistory = (history: Message[], preferredPanRecordId?: string | null): SavedPanRecord | null => {
  const allRecords = loadPanRecords();
  const recordMap = new Map(allRecords.map((item) => [item.id, item]));
  const candidateIds = [
    String(preferredPanRecordId || '').trim(),
    ...history
      .map((item) => String(item.panRecordId || '').trim())
      .filter(Boolean)
      .reverse(),
  ];
  for (const id of candidateIds) {
    const hit = recordMap.get(id);
    if (hit) return hit;
  }
  return null;
};

const mergeHistoryTurns = (history: Message[]): Array<{ role: 'user' | 'assistant'; content: string }> => {
  const merged: Array<{ role: 'user' | 'assistant'; content: string }> = [];
  history
    .filter((item) => item.role === 'user' || item.role === 'assistant')
    .forEach((item) => {
      const content = String(item.content || '').trim();
      if (!content) return;
      const last = merged[merged.length - 1];
      if (last && last.role === item.role) {
        last.content = `${last.content}\n\n${content}`.trim();
        return;
      }
      merged.push({ role: item.role, content });
    });
  return merged;
};

const splitStems = (text: string): string[] => String(text || '').split('').filter((ch) => STEM_SET.has(ch));

const buildWencePillarItems = (record: SavedPanRecord): Array<{ label: string; value: string }> => {
  const pan = record.pan;
  const items: Array<{ label: string; value: string }> = [
    { label: '年柱', value: String(pan.yearPillar || '-').trim() || '-' },
    { label: '月柱', value: String(pan.monthPillar || '-').trim() || '-' },
    { label: '日柱', value: String(pan.dayPillar || '-').trim() || '-' },
    { label: '时柱', value: String(pan.hourPillar || '-').trim() || '-' },
  ];
  if (record.panMode === 'ke') {
    items.push({ label: '刻柱', value: String(pan.kePillar || pan.hourPillar || '-').trim() || '-' });
  }
  return items;
};

const hasPanCtaCue = (text: string): boolean => {
  const clean = String(text || '').replace(/\s+/g, '');
  if (!clean) return false;
  return PAN_CTA_HINT_PATTERNS.some((pattern) => pattern.test(clean));
};

const hasPanCtaNegativeCue = (text: string): boolean => {
  const clean = String(text || '').replace(/\s+/g, '');
  if (!clean) return false;
  return /(还没起局|先不解盘|先别起局|不方便起局|暂不起局)/.test(clean);
};

const sleep = (ms: number): Promise<void> => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

/** 问测聊天气泡内嵌的九宫格（与前端奇门起局同序同构） */
const WencePanGrid: React.FC<{ record: SavedPanRecord }> = ({ record }) => {
  const pan = record.pan;
  const pillarItems = buildWencePillarItems(record);
  const palaceMap = new Map<number, NonNullable<SavedPanRecord['pan']['palaces']>[number][1]>(
    Array.isArray(pan.palaces) ? pan.palaces : [],
  );

  const renderStem = (stemText: string, options: { hasXing: boolean; inTomb: boolean }) => {
    const stems = splitStems(stemText);
    if (!stems.length) return <span className="wence-pan-stem-default">{stemText || '-'}</span>;
    return stems.map((stem, index) => {
      const toneClass = options.hasXing && options.inTomb
        ? 'wence-pan-stem-xingmu'
        : options.hasXing
          ? 'wence-pan-stem-xing'
          : options.inTomb
            ? 'wence-pan-stem-mu'
            : 'wence-pan-stem-default';
      return (
        <span key={`${stem}-${index}`} className={toneClass}>
          {stem}
        </span>
      );
    });
  };

  return (
    <div className="wence-pan-card">
      <div className="wence-pan-summary-grid">
        <div className="wence-pan-summary-label">事项</div>
        <div className="wence-pan-summary-value">{String(record.question || pan.question || '').trim() || '未填写事项'}</div>
        <div className="wence-pan-summary-label">日期</div>
        <div className="wence-pan-summary-value">{pan.timeNodeText || record.sourceDateTime || '-'}</div>
        <div className="wence-pan-summary-label">类型</div>
        <div className="wence-pan-summary-value">
          {record.panMode === 'ke' ? '刻盘' : '时盘'}
          {' · '}
          {pan.juText || '-'}
        </div>
      </div>

      <div className="wence-pan-top5">
        <div><span>旬首</span><strong>{pan.xunShou || '-'}</strong></div>
        <div><span>值符</span><strong>{pan.zhiFu || '-'}</strong></div>
        <div><span>值使</span><strong>{pan.zhiShi || '-'}</strong></div>
        <div><span>马星</span><strong>{String(pan.maText || '-').replace(/（.*?）/g, '').trim() || '-'}</strong></div>
        <div><span>空亡</span><strong>{pan.kongText || '-'}</strong></div>
      </div>

      <div className={`wence-pan-pillars ${pillarItems.length >= 5 ? 'five' : ''}`}>
        {pillarItems.map((item) => (
          <div key={item.label} className="wence-pan-pillar-item">
            <span>{item.label}</span>
            <strong>{item.value || '-'}</strong>
          </div>
        ))}
      </div>

      <div className="wence-pan-board">
        <div className="wence-pan-grid-inner">
          {PAN_GRID_POSITIONS.map((pos) => {
            const cell = palaceMap.get(pos);
            const isCenter = pos === 5;
            const gateClass = cell?.hasMenPo ? ' wence-pan-gate-menpo' : '';
            const heavenHasXing = Boolean(cell?.heavenlyHasXing);
            const earthHasXing = Boolean(cell?.earthlyHasXing || cell?.earthlyHasXingByFuYin);
            const heavenInTomb = Boolean(cell?.heavenlyInTomb);
            const earthInTomb = Boolean(cell?.earthlyInTomb);
            return (
              <div key={pos} className={`wence-pan-cell ${isCenter ? 'wence-pan-cell-center' : ''}`}>
                {!isCenter && (
                  <>
                    {cell?.hasVoid && <span className="wence-pan-void">○</span>}
                    <span className="wence-pan-deity">{cell?.deitySymbol || '-'}</span>
                    <div className="wence-pan-mid">
                      <span className="wence-pan-heaven">
                        {renderStem(cell?.heavenlyStem || '-', { hasXing: heavenHasXing, inTomb: heavenInTomb })}
                      </span>
                      <span className="wence-pan-star">{cell?.starSymbol || '-'}</span>
                    </div>
                    <span className="wence-pan-earth">
                      {renderStem(cell?.earthlyStem || '-', { hasXing: earthHasXing, inTomb: earthInTomb })}
                    </span>
                    <span className={`wence-pan-gate${gateClass}`}>{cell?.gateSymbol || '-'}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {PAN_OUTER_POSITIONS.map((item) => {
          const leadStem = palaceMap.get(item.position)?.leadStem || '';
          if (!leadStem) return null;
          const alignClass = item.side === 'left'
            ? 'wence-pan-outer-inner-left'
            : item.side === 'right'
              ? 'wence-pan-outer-inner-right'
              : 'wence-pan-outer-inner-center';
          return (
            <span key={item.position} className={`wence-pan-outer-stem ${item.className}`}>
              <span className={`wence-pan-outer-inner ${alignClass}`}>{leadStem}</span>
            </span>
          );
        })}

        {(() => {
          const horsePos = Number(pan.horsePosition || 0);
          const horsePosClass = PAN_HORSE_CORNER_CLASS_MAP[horsePos];
          if (!horsePosClass) return null;
          return (
            <span className={`wence-pan-horse ${horsePosClass}`}>
              <span className="wence-pan-horse-inner">马</span>
            </span>
          );
        })()}
      </div>

      <div className="wence-pan-legend">
        颜色说明：<span className="wence-pan-stem-mu">入墓</span>、
        <span className="wence-pan-stem-xing">击刑</span>、
        <span className="wence-pan-gate-menpo">门迫</span>、
        <span className="wence-pan-stem-xingmu">刑+墓</span>；○为空亡。
      </div>
    </div>
  );
};

const parseWenceDebugFromResponse = (response: { text?: string; full?: unknown }): WenceDebugInfo | null => {
  const payload = response?.full && typeof response.full === 'object'
    ? response.full as { meta?: unknown }
    : null;
  const meta = payload?.meta && typeof payload.meta === 'object'
    ? payload.meta as Record<string, unknown>
    : null;
  if (!meta) return null;
  const modelText = [meta.modelSelector, meta.model]
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .join(' / ');
  const raw = meta.wenceDebug && typeof meta.wenceDebug === 'object'
    ? meta.wenceDebug as Record<string, unknown>
    : null;
  const toNullableNumber = (value: unknown): number | null => (
    Number.isFinite(Number(value)) ? Number(value) : null
  );
  return {
    modelText,
    featurePromptPreview: String(raw?.featurePromptPreview || ''),
    thinkingSystemPromptPreview: String(raw?.thinkingSystemPromptPreview || ''),
    thinkingUserPromptPreview: String(raw?.thinkingUserPromptPreview || ''),
    chainPromptPreview: String(raw?.chainPromptPreview || ''),
    chainPromptChars: toNullableNumber(raw?.chainPromptChars),
    chainPromptSectionCount: toNullableNumber(raw?.chainPromptSectionCount),
    promptMode: String(raw?.promptMode || ''),
    thinkingResult: raw?.thinkingResult && typeof raw.thinkingResult === 'object'
      ? raw.thinkingResult as Record<string, unknown>
      : null,
    twoStepThinkingEnabled: raw?.twoStepThinkingEnabled === true,
    twoStepThinkingSuccess: raw?.twoStepThinkingSuccess === true,
    twoStepThinkingFallbackUsed: raw?.twoStepThinkingFallbackUsed === true,
    twoStepThinkingFallbackReason: String(raw?.twoStepThinkingFallbackReason || ''),
    twoStepThinkingError: String(raw?.twoStepThinkingError || ''),
    knowledgeRefs: Array.isArray(raw?.knowledgeRefs)
      ? raw.knowledgeRefs
        .filter((item): item is { title?: string; source?: string; reason?: string } => Boolean(item && typeof item === 'object'))
        .map((item) => ({
          title: typeof item.title === 'string' ? item.title : '',
          source: typeof item.source === 'string' ? item.source : '',
          reason: typeof item.reason === 'string' ? item.reason : '',
        }))
      : [],
    knowledgeQueryUsed: String(raw?.knowledgeQueryUsed || ''),
    durationMs: toNullableNumber(raw?.durationMs),
    promptPolicy: String(raw?.promptPolicy || ''),
    promptPolicyReason: String(raw?.promptPolicyReason || ''),
    featurePromptChars: toNullableNumber(raw?.featurePromptChars),
    finishReason: String(raw?.finishReason || meta.finishReason || ''),
    tokenUsage: raw?.tokenUsage && typeof raw.tokenUsage === 'object'
      ? {
        promptTokens: toNullableNumber((raw.tokenUsage as Record<string, unknown>).promptTokens),
        completionTokens: toNullableNumber((raw.tokenUsage as Record<string, unknown>).completionTokens),
        reasoningTokens: toNullableNumber((raw.tokenUsage as Record<string, unknown>).reasoningTokens),
        totalTokens: toNullableNumber((raw.tokenUsage as Record<string, unknown>).totalTokens),
      }
      : {
        promptTokens: toNullableNumber(meta.promptTokens),
        completionTokens: toNullableNumber(meta.completionTokens),
        reasoningTokens: toNullableNumber(meta.reasoningTokens),
        totalTokens: toNullableNumber(meta.totalTokens),
      },
    sanitizeConfig: raw?.sanitizeConfig && typeof raw.sanitizeConfig === 'object'
      ? {
        bracketSanitizeEnabled: (raw.sanitizeConfig as Record<string, unknown>).bracketSanitizeEnabled !== false,
        bracketGuidanceKeywords: String((raw.sanitizeConfig as Record<string, unknown>).bracketGuidanceKeywords || ''),
        dualLayer: (raw.sanitizeConfig as Record<string, unknown>).dualLayer === true,
        source: String((raw.sanitizeConfig as Record<string, unknown>).source || ''),
      }
      : null,
    outputConfig: raw?.outputConfig && typeof raw.outputConfig === 'object'
      ? raw.outputConfig as Record<string, unknown>
      : null,
  };
};

const formatDebugJson = (value: unknown): string => {
  if (!value) return '暂无';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const resolveWenceModeTimeoutMs = (mode: string): number => {
  if (mode === 'post_pan_bootstrap') return 95000;
  if (mode === 'post_pan_followup') return 75000;
  return 30000;
};

const WENCE_MODE_LABELS: Record<string, string> = {
  intake: '起局前问询',
  post_pan_bootstrap: '起局后首轮',
  post_pan_followup: '起局后追问',
};

const formatTokenMetric = (value: number | null | undefined): string => (
  Number.isFinite(Number(value)) ? String(Math.round(Number(value))) : '-'
);

const formatDurationMetric = (value: number | null | undefined): string => {
  const duration = Number(value);
  if (!Number.isFinite(duration) || duration < 0) return '-';
  if (duration < 1000) return `${Math.round(duration)} ms`;
  if (duration < 10000) return `${(duration / 1000).toFixed(2)} s`;
  return `${(duration / 1000).toFixed(1)} s`;
};

const formatDebugTimestamp = (raw: string): string => {
  const ts = new Date(raw);
  if (!Number.isFinite(ts.getTime())) return '-';
  return ts.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatWenceModeLabel = (mode: string): string => (
  WENCE_MODE_LABELS[String(mode || '').trim()] || (String(mode || '').trim() || '未标记阶段')
);

const formatWencePromptModeLabel = (mode: string): string => {
  const value = String(mode || '').trim().toLowerCase();
  if (value === 'auto' || value === 'lite' || value === 'fast' || value === 'quick' || value === '快速') return '快速';
  if (value === 'full' || value === 'accurate' || value === 'high_precision' || value === '高精度') return '高精度';
  return String(mode || '').trim() || '-';
};

const formatSectionRows = (rows?: Array<{ label: string; value: string }>): string => (
  Array.isArray(rows) && rows.length
    ? rows.map((row) => `${row.label}：${row.value || '-'}`).join('\n')
    : '暂无'
);

const buildKnowledgeRefsText = (refs: Array<{ title?: string; source?: string; reason?: string }>): string => {
  if (!Array.isArray(refs) || !refs.length) return '暂无';
  return refs.map((item, index) => [
    `${index + 1}. ${String(item?.title || '未命名条目').trim() || '未命名条目'}`,
    `来源：${String(item?.source || '-').trim() || '-'}`,
    `命中理由：${String(item?.reason || '-').trim() || '-'}`,
  ].join('\n')).join('\n\n');
};

const buildDebugTurnDetailText = (item: WenceDebugHistoryItem, index: number): string => [
  `序号：${index + 1}`,
  `时间：${formatDebugTimestamp(item.ts)}`,
  `阶段：${formatWenceModeLabel(item.mode)}`,
  `耗时：${formatDurationMetric(item.durationMs)}`,
  `输入 Token：${formatTokenMetric(item.tokenUsage.promptTokens)}`,
  `输出 Token：${formatTokenMetric(item.tokenUsage.completionTokens)}`,
  `推理 Token：${formatTokenMetric(item.tokenUsage.reasoningTokens)}`,
  `总 Token：${formatTokenMetric(item.tokenUsage.totalTokens)}`,
  `知识命中：${String(item.knowledgeRefCount ?? 0)}`,
  `提示策略：${String(item.promptPolicy || '-').trim() || '-'}`,
  `finish_reason：${String(item.finishReason || '-').trim() || '-'}`,
  '',
  '用户消息：',
  String(item.userText || '').trim() || '-',
  '',
  '回复预览：',
  String(item.assistantPreview || '').trim() || '-',
].join('\n');

const buildDebugHistorySummaryText = (history: WenceDebugHistoryItem[]): string => {
  if (!Array.isArray(history) || !history.length) return '暂无历史调试记录';
  return history.map((item, index) => buildDebugTurnDetailText(item, index)).join('\n\n--------------------\n\n');
};

const WenceDebugPage: React.FC<{
  appLabel: string;
  debug: WenceDebugInfo | null;
  history: WenceDebugHistoryItem[];
  onClose: () => void;
  onSubmitClientLog: () => Promise<void> | void;
  clientLogSubmitting: boolean;
  clientLogSubmitResult: { ok: boolean; msg: string; logId?: string } | null;
  onDismissClientLogResult: () => void;
}> = ({
  appLabel,
  debug,
  history,
  onClose,
  onSubmitClientLog,
  clientLogSubmitting,
  clientLogSubmitResult,
  onDismissClientLogResult,
}) => {
  const [fullscreenSection, setFullscreenSection] = useState<{ title: string; content: string } | null>(null);
  const [copiedSectionTitle, setCopiedSectionTitle] = useState('');
  const [activeSectionId, setActiveSectionId] = useState('summary');
  const [isFullView, setIsFullView] = useState(false);
  const fallbackDebug: WenceDebugInfo = {
    modelText: '',
    featurePromptPreview: '',
    thinkingSystemPromptPreview: '',
    thinkingUserPromptPreview: '',
    chainPromptPreview: '',
    chainPromptChars: null,
    chainPromptSectionCount: null,
    promptMode: '',
    thinkingResult: null,
    twoStepThinkingEnabled: false,
    twoStepThinkingSuccess: false,
    twoStepThinkingFallbackUsed: false,
    twoStepThinkingFallbackReason: '',
    twoStepThinkingError: '',
    knowledgeRefs: [],
    knowledgeQueryUsed: '',
    durationMs: null,
    promptPolicy: '',
    promptPolicyReason: '',
    featurePromptChars: null,
    finishReason: '',
    tokenUsage: {
      promptTokens: null,
      completionTokens: null,
      reasoningTokens: null,
      totalTokens: null,
    },
    sanitizeConfig: null,
    outputConfig: null,
  };
  const effectiveDebug = debug || fallbackDebug;
  const twoStepStatus = effectiveDebug.twoStepThinkingEnabled
    ? (effectiveDebug.twoStepThinkingSuccess ? '已启用（成功）' : `已启用（失败：${effectiveDebug.twoStepThinkingError || '未知'}）`)
    : '未启用';
  const fallbackStatus = effectiveDebug.twoStepThinkingFallbackUsed
    ? `；本地兜底：${effectiveDebug.twoStepThinkingFallbackReason || '上游未返回有效JSON'}`
    : '';
  const mergedThinkingPrompt = [
    effectiveDebug.thinkingSystemPromptPreview ? `【system】\n${effectiveDebug.thinkingSystemPromptPreview}` : '',
    effectiveDebug.thinkingUserPromptPreview ? `【user模板】\n${effectiveDebug.thinkingUserPromptPreview}` : '',
  ].filter(Boolean).join('\n\n');
  const promptSections = [
    {
      label: '角色设定',
      content: effectiveDebug.featurePromptPreview || '暂无内容',
    },
    {
      label: '第一段提示词',
      content: mergedThinkingPrompt || (effectiveDebug.twoStepThinkingEnabled ? '暂无内容' : '当前轮未触发两段思考'),
    },
    {
      label: '最终 Chain Prompt',
      content: effectiveDebug.chainPromptPreview || '当前轮未回传最终 chain prompt',
    },
  ];
  const knowledgeSections = [
    {
      label: '思维链路',
      content: formatDebugJson(effectiveDebug.thinkingResult),
    },
    {
      label: '知识命中',
      content: buildKnowledgeRefsText(effectiveDebug.knowledgeRefs),
    },
  ];
  const strategyRows = [
    { label: '两段思考', value: `${twoStepStatus}${fallbackStatus}` },
    { label: 'finish_reason', value: effectiveDebug.finishReason || '-' },
    { label: '提示模式', value: formatWencePromptModeLabel(effectiveDebug.promptMode || '-') },
    {
      label: '括号清洗',
      value: effectiveDebug.sanitizeConfig
        ? `${effectiveDebug.sanitizeConfig.bracketSanitizeEnabled === false ? '关闭' : '开启'}${effectiveDebug.sanitizeConfig.bracketGuidanceKeywords ? `（关键词：${effectiveDebug.sanitizeConfig.bracketGuidanceKeywords.replace(/\n+/g, '、')}）` : ''}${effectiveDebug.sanitizeConfig.source ? ` [${effectiveDebug.sanitizeConfig.source}]` : ''}`
        : '未回传',
    },
    {
      label: '提示策略',
      value: `${effectiveDebug.promptPolicy || '-'}${effectiveDebug.promptPolicyReason ? ` (${effectiveDebug.promptPolicyReason})` : ''}`,
    },
    { label: '提示词长度', value: String(effectiveDebug.featurePromptChars ?? '-') },
    {
      label: 'Chain Prompt',
      value: `${String(effectiveDebug.chainPromptChars ?? '-')} 字 / ${String(effectiveDebug.chainPromptSectionCount ?? '-')} 段`,
    },
  ];
  const retrievalRows = [
    { label: '检索词', value: effectiveDebug.knowledgeQueryUsed || '-' },
    { label: '命中条数', value: String(effectiveDebug.knowledgeRefs.length || 0) },
  ];
  const summaryStats = [
    {
      label: '模型',
      value: effectiveDebug.modelText || '-',
      className: 'border-blue-100 bg-blue-50 text-blue-700',
    },
    {
      label: '总 Token',
      value: formatTokenMetric(effectiveDebug.tokenUsage.totalTokens),
      className: 'border-indigo-100 bg-indigo-50 text-indigo-700',
    },
    {
      label: '输入',
      value: formatTokenMetric(effectiveDebug.tokenUsage.promptTokens),
      className: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    },
    {
      label: '输出',
      value: formatTokenMetric(effectiveDebug.tokenUsage.completionTokens),
      className: 'border-orange-100 bg-orange-50 text-orange-700',
    },
  ];
  const summaryPills = [
    { label: '耗时', value: formatDurationMetric(effectiveDebug.durationMs), className: 'border-slate-200 bg-white text-slate-700' },
    { label: '推理', value: formatTokenMetric(effectiveDebug.tokenUsage.reasoningTokens), className: 'border-violet-100 bg-violet-50 text-violet-700' },
    { label: '阶段', value: history[0] ? formatWenceModeLabel(history[0].mode) : '-', className: 'border-cyan-100 bg-cyan-50 text-cyan-700' },
    { label: '知识命中', value: String(effectiveDebug.knowledgeRefs.length || 0), className: 'border-teal-100 bg-teal-50 text-teal-700' },
    { label: '提示模式', value: formatWencePromptModeLabel(effectiveDebug.promptMode || '-'), className: 'border-amber-100 bg-amber-50 text-amber-700' },
    { label: 'finish_reason', value: effectiveDebug.finishReason || '-', className: 'border-rose-100 bg-rose-50 text-rose-700' },
  ];
  const fullLogTitle = '本次完整日志';
  const fullLogText = formatDebugJson({
    generatedAt: new Date().toISOString(),
    appLabel,
    latestDebug: effectiveDebug,
    debugHistory: history,
    promptSections,
    knowledgeSections,
    retrievalRows,
    strategyRows,
    outputConfig: effectiveDebug.outputConfig,
  });
  type DebugPanelCard = {
    id: string;
    navLabel: string;
    title: string;
    subTitle: string;
    icon: React.ComponentType<{ size?: number; className?: string }>;
    copyText: string;
    canExpand?: boolean;
    renderBody: () => React.ReactNode;
  };
  const handleCopy = useCallback(async (title: string, content: string) => {
    const text = String(content || '');
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopiedSectionTitle(title);
      window.setTimeout(() => {
        setCopiedSectionTitle((prev) => (prev === title ? '' : prev));
      }, 1400);
    } catch (_) {}
  }, []);
  const jumpToSection = useCallback((sectionId: string) => {
    setActiveSectionId(sectionId);
  }, []);
  const openSectionFullscreen = useCallback((title: string, content: string) => {
    setFullscreenSection({ title, content: String(content || '暂无') || '暂无' });
  }, []);
  const iconButtonClass = 'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:opacity-50';
  const primaryIconButtonClass = 'inline-flex h-9 w-9 items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600 transition hover:bg-indigo-100 disabled:opacity-50';
  const renderTextBlock = useCallback((label: string, content: string, options?: { mono?: boolean; badge?: string }) => (
    <div className="space-y-2.5">
      <div className="flex min-w-0 items-center gap-2">
        <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-700">{label}</h4>
          {options?.badge ? (
            <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-black text-emerald-700">
              {options.badge}
            </span>
          ) : null}
      </div>
      <div className={`border border-slate-200 bg-slate-50/70 p-4 text-slate-800 ${options?.mono ? 'font-mono text-[12px] leading-6' : 'text-[14px] font-semibold leading-7'}`}>
        <div className="whitespace-pre-wrap break-words">{content || '暂无内容'}</div>
      </div>
    </div>
  ), []);
  const renderInfoRows = useCallback((rows: Array<{ label: string; value: string }>) => (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {rows.map((row) => (
        <div key={row.label} className="border border-slate-200 bg-slate-50/70 p-4">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">{row.label}</p>
          <p className="mt-2 whitespace-pre-wrap break-words text-[14px] font-semibold leading-6 text-slate-800">{row.value || '-'}</p>
        </div>
      ))}
    </div>
  ), []);
  const cards: DebugPanelCard[] = [
    {
      id: 'summary',
      navLabel: '摘要',
      title: '统计摘要',
      subTitle: '本轮模型调用核心指标',
      icon: BarChart3,
      copyText: [
        `模型：${effectiveDebug.modelText || '-'}`,
        `总 Token：${formatTokenMetric(effectiveDebug.tokenUsage.totalTokens)}`,
        `输入：${formatTokenMetric(effectiveDebug.tokenUsage.promptTokens)}`,
        `输出：${formatTokenMetric(effectiveDebug.tokenUsage.completionTokens)}`,
        `推理：${formatTokenMetric(effectiveDebug.tokenUsage.reasoningTokens)}`,
        `耗时：${formatDurationMetric(effectiveDebug.durationMs)}`,
      ].join('\n'),
      renderBody: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {summaryStats.map((stat) => (
              <div key={stat.label} className={`flex min-h-[96px] flex-col justify-between rounded-[22px] border-2 p-4 transition-all ${stat.className}`}>
                <p className="text-[10px] font-black uppercase tracking-tight opacity-60">{stat.label}</p>
                <p className="break-all text-sm font-black leading-tight sm:text-base">{stat.value}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {summaryPills.map((pill) => (
              <div key={pill.label} className={`rounded-[18px] border px-4 py-3 ${pill.className}`}>
                <p className="text-[10px] font-black uppercase tracking-[0.16em] opacity-70">{pill.label}</p>
                <p className="mt-1 break-words text-[13px] font-bold leading-5">{pill.value}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'prompts',
      navLabel: '提示词',
      title: '提示词',
      subTitle: '角色设定、第一段逻辑与最终 chain',
      icon: Cpu,
      copyText: promptSections.map((section) => `[${section.label}]\n${section.content || '暂无内容'}`).join('\n\n'),
      renderBody: () => (
        <div className="space-y-4">
          {renderTextBlock(promptSections[0].label, promptSections[0].content, { mono: false })}
          <div className="border border-slate-200 bg-slate-50/70">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-700">第一段提示词</h4>
                <p className="mt-1 text-xs font-medium text-slate-500">默认折叠，点击右上角放大查看。</p>
              </div>
              <button
                type="button"
                aria-label="放大查看第一段提示词"
                title="放大查看第一段提示词"
                onClick={() => openSectionFullscreen('第一段提示词', promptSections[1].content)}
                className={iconButtonClass}
              >
                <Maximize2 size={14} />
              </button>
            </div>
          </div>
          <div className="border border-slate-200 bg-slate-50/70">
            <div className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-700">最终 Chain Prompt</h4>
                <p className="mt-1 text-xs font-medium text-slate-500">后端实际喂给本轮的 system chain，点击右上角放大查看。</p>
              </div>
              <button
                type="button"
                aria-label="放大查看最终 Chain Prompt"
                title="放大查看最终 Chain Prompt"
                onClick={() => openSectionFullscreen('最终 Chain Prompt', promptSections[2].content)}
                className={iconButtonClass}
              >
                <Maximize2 size={14} />
              </button>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'knowledge',
      navLabel: '知识库',
      title: '知识库',
      subTitle: '思维链路与命中详情',
      icon: Database,
      copyText: [
        '[检索信息]',
        formatSectionRows(retrievalRows),
        '',
        ...knowledgeSections.map((section) => `[${section.label}]\n${section.content || '暂无内容'}`),
      ].join('\n'),
      renderBody: () => (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {retrievalRows.map((row) => (
              <div key={row.label} className="rounded-[20px] border border-indigo-100 bg-indigo-50/70 p-4">
                <div className="flex items-center gap-2 text-indigo-700">
                  <Search size={15} />
                  <p className="text-[11px] font-black uppercase tracking-[0.18em]">{row.label}</p>
                </div>
                <p className="mt-2 break-words text-[14px] font-semibold leading-6 text-slate-800">{row.value || '-'}</p>
              </div>
            ))}
          </div>
          {knowledgeSections.map((section) => (
            <React.Fragment key={section.label}>
              {renderTextBlock(section.label, section.content, {
                mono: section.label === '思维链路',
                badge: section.label === '知识命中' && effectiveDebug.knowledgeRefs.length > 0 ? '命中' : undefined,
              })}
            </React.Fragment>
          ))}
        </div>
      ),
    },
    {
      id: 'strategy',
      navLabel: '策略',
      title: '策略配置',
      subTitle: '提示策略与输出逻辑',
      icon: Settings,
      copyText: [
        '[策略配置]',
        formatSectionRows(strategyRows),
        '',
        '[输出配置]',
        formatDebugJson(effectiveDebug.outputConfig),
      ].join('\n'),
      renderBody: () => (
        <div className="space-y-6">
          {renderInfoRows(strategyRows)}
          {renderTextBlock('输出配置', formatDebugJson(effectiveDebug.outputConfig), { mono: true })}
        </div>
      ),
    },
    {
      id: 'recent-chat',
      navLabel: '对话',
      title: '最近对话',
      subTitle: '历史交互轨迹',
      icon: MessageSquareText,
      copyText: buildDebugHistorySummaryText(history),
      canExpand: history.length > 0,
      renderBody: () => (
        <div className="space-y-4">
          {!history.length && (
            <div className="border border-slate-200 bg-slate-50/70 p-4 text-sm font-medium leading-7 text-slate-600">
              暂无历史调试记录。
            </div>
          )}
          {history.map((item, index) => {
            const turnTitle = `${formatWenceModeLabel(item.mode)} · ${formatDebugTimestamp(item.ts)}`;
            const turnText = buildDebugTurnDetailText(item, index);
            return (
              <article key={item.id} className="border border-slate-200 bg-slate-50/70 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="text-sm font-black text-slate-900">{turnTitle}</h4>
                    <p className="mt-1 text-xs font-semibold text-slate-500">第 {history.length - index} 条调试记录</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { void handleCopy(turnTitle, turnText); }}
                      aria-label="复制最近对话"
                      title="复制最近对话"
                      className={iconButtonClass}
                    >
                      <Copy size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => openSectionFullscreen(turnTitle, turnText)}
                      aria-label="放大最近对话"
                      title="放大最近对话"
                      className={iconButtonClass}
                    >
                      <Maximize2 size={13} />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-[11px] font-black text-blue-700">{`输入 ${formatTokenMetric(item.tokenUsage.promptTokens)}`}</span>
                  <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-1 text-[11px] font-black text-emerald-700">{`输出 ${formatTokenMetric(item.tokenUsage.completionTokens)}`}</span>
                  <span className="rounded-full border border-violet-100 bg-violet-50 px-2.5 py-1 text-[11px] font-black text-violet-700">{`推理 ${formatTokenMetric(item.tokenUsage.reasoningTokens)}`}</span>
                  <span className="rounded-full border border-orange-100 bg-orange-50 px-2.5 py-1 text-[11px] font-black text-orange-700">{`耗时 ${formatDurationMetric(item.durationMs)}`}</span>
                  <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-black text-slate-600">{`知识 ${String(item.knowledgeRefCount ?? 0)}`}</span>
                </div>
                <div className="mt-4 space-y-3">
                  <div className="border border-slate-200 bg-white p-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">用户消息</p>
                    <p className="mt-2 whitespace-pre-wrap break-words text-[14px] font-semibold leading-6 text-slate-800">{item.userText || '-'}</p>
                  </div>
                  <div className="border border-slate-200 bg-white p-4">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">回复预览</p>
                    <p className="mt-2 whitespace-pre-wrap break-words text-[14px] font-semibold leading-6 text-slate-800">{item.assistantPreview || '-'}</p>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ),
    },
  ];
  const activeCard = cards.find((card) => card.id === activeSectionId) || cards[0];
  const renderCard = (card: DebugPanelCard): React.ReactNode => {
    const Icon = card.icon;
    return (
      <section
        key={card.id}
        className="mb-3 overflow-hidden border border-slate-200 bg-white sm:mb-4"
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center text-slate-700">
              <Icon size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-base font-black text-slate-900">{card.title}</h3>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              aria-label={`复制${card.title}`}
              title={`复制${card.title}`}
              onClick={() => { void handleCopy(card.title, card.copyText); }}
              className={copiedSectionTitle === card.title ? primaryIconButtonClass : iconButtonClass}
            >
              <Copy size={14} />
            </button>
            {card.canExpand !== false && (
              <button
                type="button"
                aria-label={`放大${card.title}`}
                title={`放大${card.title}`}
                onClick={() => openSectionFullscreen(card.title, card.copyText)}
                className={iconButtonClass}
              >
                <Maximize2 size={14} />
              </button>
            )}
          </div>
        </div>
        <div className="p-4 sm:p-5">{card.renderBody()}</div>
      </section>
    );
  };
  return (
    <div className="fixed inset-0 z-[1300] flex flex-col overflow-hidden bg-slate-50 text-slate-900" role="dialog" aria-modal="true">
      <header className="flex min-h-[58px] flex-none items-center justify-between gap-3 border-b border-slate-200 bg-white px-3 py-2 sm:px-5">
        <div className="min-w-0">
          <div className="text-[11px] font-semibold tracking-[0.14em] text-slate-500">{appLabel}</div>
          <h1 className="truncate text-base font-black tracking-tight text-slate-900">内部调试</h1>
        </div>
        <div className="flex shrink-0 flex-nowrap items-center gap-1.5">
          <button
            type="button"
            aria-label={isFullView ? '切换单卡' : '纵览全文'}
            title={isFullView ? '切换单卡' : '纵览全文'}
            onClick={() => setIsFullView((prev) => !prev)}
            className={isFullView ? primaryIconButtonClass : iconButtonClass}
          >
            {isFullView ? <Rows size={16} /> : <LayoutGrid size={16} />}
          </button>
          <button
            type="button"
            aria-label="复制完整日志"
            title="复制完整日志"
            className={copiedSectionTitle === fullLogTitle ? primaryIconButtonClass : iconButtonClass}
            onClick={() => { void handleCopy(fullLogTitle, fullLogText); }}
          >
            <Copy size={15} />
          </button>
          <button
            type="button"
            aria-label={clientLogSubmitting ? '提交中' : '提交日志'}
            title={clientLogSubmitting ? '提交中' : '提交日志'}
            className={primaryIconButtonClass}
            onClick={() => { void onSubmitClientLog(); }}
            disabled={clientLogSubmitting}
          >
            <Send size={15} />
          </button>
          <button
            type="button"
            aria-label="关闭"
            title="关闭"
            className={iconButtonClass}
            onClick={onClose}
          >
            <X size={15} />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <aside className="hidden w-64 shrink-0 flex-col overflow-y-auto border-r border-slate-200 bg-white pt-6 lg:flex">
          <div className="mb-4 px-6 text-[11px] font-black uppercase tracking-[0.25em] text-indigo-400">Navigation</div>
          <nav className="space-y-2 px-4">
            {cards.map((card) => {
              const Icon = card.icon;
              const isActive = activeSectionId === card.id;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => jumpToSection(card.id)}
                  className={`flex w-full items-center gap-3.5 rounded-2xl px-5 py-4 text-left text-sm font-black transition-all ${
                    isActive
                      ? 'translate-x-1 bg-indigo-600 text-white shadow-xl shadow-indigo-100'
                      : 'text-slate-400 hover:bg-indigo-50 hover:text-indigo-600'
                  }`}
                >
                  <Icon size={18} />
                  <span>{card.navLabel}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 overflow-y-auto bg-slate-50/70 p-2 sm:p-3 md:p-4">
          <div className="mx-auto max-w-5xl">
            {!debug && (
              <div className="mb-3 border border-amber-100 bg-amber-50/80 p-4 sm:mb-4">
                <div className="flex items-start gap-3 text-amber-700">
                  <AlertCircle size={20} className="mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-black">当前会话还没有拿到调试数据</p>
                    <p className="mt-1 text-sm font-medium leading-6 text-amber-800">
                      这通常表示没有命中本地问测后端链路，或者当前轮还没完成一次有效请求。
                    </p>
                  </div>
                </div>
              </div>
            )}
            {clientLogSubmitResult && !clientLogSubmitResult.ok && (
              <div className="mb-3 border border-rose-200 bg-rose-50/90 p-4 sm:mb-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 text-rose-700">
                    <AlertCircle size={18} className="mt-0.5 shrink-0" />
                    <p className="text-sm font-semibold leading-6">{clientLogSubmitResult.msg}</p>
                  </div>
                  <button
                    type="button"
                    aria-label="关闭提示"
                    title="关闭提示"
                    onClick={onDismissClientLogResult}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-rose-200 bg-white text-rose-600"
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}
            {isFullView ? cards.map((card) => renderCard(card)) : renderCard(activeCard)}
            <div className="h-20" />
          </div>
        </main>
      </div>
      {!isFullView && (
        <div className="fixed bottom-4 left-3 right-3 z-[1310] lg:hidden">
          <nav className="mx-auto flex max-w-[460px] items-stretch justify-between rounded-[20px] border border-slate-200 bg-white p-1.5 shadow-sm">
            {cards.map((card) => {
              const Icon = card.icon;
              const isActive = activeSectionId === card.id;
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => setActiveSectionId(card.id)}
                  className={`flex min-w-0 flex-1 flex-col items-center justify-center rounded-[14px] px-1 py-2 transition-all duration-200 ${
                    isActive ? 'bg-slate-900 text-white' : 'text-slate-500 active:bg-slate-100'
                  }`}
                >
                  <div className={`${isActive ? 'mb-0.5 text-white' : 'text-slate-400'} transition-all duration-200`}>
                    <Icon size={18} />
                  </div>
                  <span className={`${isActive ? 'text-white' : 'text-slate-400'} text-[10px] font-black tracking-tight`}>
                    {card.navLabel}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>
      )}
      {fullscreenSection && (
        <div className="fixed inset-0 z-[1315] flex flex-col bg-white" role="dialog" aria-modal="true">
          <div className="flex min-h-14 items-center justify-between gap-4 border-b border-slate-200 px-4 py-3">
              <strong className="truncate text-base font-black text-slate-900">{fullscreenSection.title}</strong>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="复制"
                  title="复制"
                  className={copiedSectionTitle === fullscreenSection.title ? primaryIconButtonClass : iconButtonClass}
                  onClick={() => { void handleCopy(fullscreenSection.title, fullscreenSection.content || '暂无'); }}
                >
                  <Copy size={14} />
                </button>
                <button
                  type="button"
                  aria-label="关闭"
                  title="关闭"
                  className={iconButtonClass}
                  onClick={() => setFullscreenSection(null)}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          <div className="flex-1 overflow-auto bg-white">
            <pre className="min-h-full whitespace-pre-wrap break-words p-4 text-[13px] leading-7 text-slate-800 sm:p-5">{fullscreenSection.content || '暂无'}</pre>
          </div>
        </div>
      )}
      {clientLogSubmitResult?.ok && clientLogSubmitResult.logId && (
        <div className="fixed inset-0 z-[1320] flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md border border-indigo-100 bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center bg-emerald-100 text-emerald-600">
                <CheckCircle2 size={22} />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-black text-slate-900">Log 提交成功</h3>
                <p className="mt-1 text-sm font-medium leading-6 text-slate-600">{clientLogSubmitResult.msg || '已生成可查询的日志编号。'}</p>
              </div>
            </div>
            <div className="mt-5 border border-indigo-100 bg-indigo-50 px-4 py-5 text-center text-lg font-black tracking-[0.06em] text-indigo-700">
              {clientLogSubmitResult.logId}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                aria-label="复制代号"
                title="复制代号"
                className={copiedSectionTitle === 'logId' ? primaryIconButtonClass : iconButtonClass}
                onClick={() => { void handleCopy('logId', clientLogSubmitResult.logId || ''); }}
              >
                <Copy size={14} />
              </button>
              <button
                type="button"
                aria-label="关闭"
                title="关闭"
                className={iconButtonClass}
                onClick={onDismissClientLogResult}
              >
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const WenceHistoryPage: React.FC<{
  appLabel: string;
  items: PersistedWenceHistoryItem[];
  onClose: () => void;
  onOpen: (item: PersistedWenceHistoryItem) => void;
  onDelete: (id: string) => void;
  onClear: () => void;
}> = ({ appLabel, items, onClose, onOpen, onDelete, onClear }) => (
  <div className="wence-debug-page" role="dialog" aria-modal="true">
    <div className="wence-debug-page-head">
      <strong>历史对话</strong>
      <div className="wence-history-head-actions">
        <button type="button" onClick={onClear} disabled={!items.length}>清空</button>
        <button type="button" onClick={onClose}>关闭</button>
      </div>
    </div>
    <div className="wence-debug-page-body">
      {!items.length && (
        <div className="wence-debug-card">
          <div className="wence-debug-page-note">暂无历史对话。点右上角刷新时会自动保存当前{appLabel}会话。</div>
        </div>
      )}
      {items.map((item) => {
        const userLast = [...item.state.messages].reverse().find((m) => m.role === 'user');
        const preview = String(userLast?.content || item.title || '未命名会话').slice(0, 90);
        return (
          <div key={item.id} className="wence-debug-card">
            <div className="wence-history-item">
              <div className="wence-history-main">
                <strong>{item.title || '未命名会话'}</strong>
                <p>{preview || '无内容'}</p>
                <span>{new Date(item.updatedAt).toLocaleString('zh-CN')}</span>
              </div>
              <div className="wence-history-actions">
                <button type="button" onClick={() => onOpen(item)}>载入</button>
                <button type="button" className="danger" onClick={() => onDelete(item.id)}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

const normalizeAssistantTextStructure = (raw: string): string => {
  let text = String(raw || '').replace(/\r\n?/g, '\n');
  if (!text) return '';
  text = text
    .replace(/^#{1,6}\s*/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/[*`]/g, '')
    .replace(/^\s*[-=]{2,}\s*$/gm, '')
    .replace(/作为(?:一个)?AI[，,]?\s*/g, '')
    .replace(/(?:小标题|标题)\s*[：:]/g, '')
    .replace(/\n{3,}/g, '\n\n');

  const lines = text.split('\n');
  const converted: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      converted.push('');
      continue;
    }
    const numberedMatch = trimmed.match(/^(\d{1,2})[\.、)\-：:]\s*(.+)$/);
    if (numberedMatch) {
      converted.push(numberedMatch[2].trim());
      continue;
    }
    const bulletMatch = trimmed.match(/^(?:[-•·]\s*)(.+)$/);
    if (bulletMatch) {
      converted.push(bulletMatch[1].trim());
      continue;
    }
    const stepMatch = trimmed.match(/^第[一二三四五六七八九十\d]+[步，、：:]\s*(.*)$/);
    if (stepMatch) {
      const rest = (stepMatch[1] || '').trim();
      if (rest) converted.push(rest);
      continue;
    }
    const orderedMatch = trimmed.match(/^第[一二三四五六七八九十\d]+[，、：:]\s*(.+)$/);
    if (orderedMatch) {
      converted.push((orderedMatch[1] || '').trim());
      continue;
    }
    const chineseOrderedMatch = trimmed.match(/^[一二三四五六七八九十]+[、.：:]\s*(.+)$/);
    if (chineseOrderedMatch) {
      converted.push((chineseOrderedMatch[1] || '').trim());
      continue;
    }
    converted.push(
      trimmed.replace(
        /^(?:结论|分析|建议|判断|原因|总结|重点|核心|方向|提醒|补充|说明|小结|行动)\s*[：:]\s*/i,
        '',
      ),
    );
  }
  text = converted.join('\n').replace(/\n{3,}/g, '\n\n').trim();
  return text;
};

const sanitizeAssistantTextForUi = (raw: string, mode: string, sanitizeConfig?: WenceUiSanitizeConfig | null): string => {
  const normalizedMode = String(mode || '').trim().toLowerCase();
  const keywordRegex = buildBracketGuidanceKeywordRegex(String(sanitizeConfig?.bracketGuidanceKeywords || ''));
  const sanitizeEnabled = sanitizeConfig?.bracketSanitizeEnabled !== false;
  let text = String(raw || '').replace(/\r\n?/g, '\n');
  for (const pattern of BANNED_DISPLAY_PATTERNS) {
    text = text.replace(pattern, '');
  }
  text = text
    .replace(/【\s*[^】]*】/g, '')
    .replace(/^孩子[，,\s]*/gm, '')
    .replace(/内部思考|推理过程|思考过程/g, '')
    .replace(/空亡下面的需求/g, '当前还没落地的需求')
    .replace(/你现在就像在泥潭里拔腿，看着在动，其实还在原地打转。?/g, '你现在推进很费劲，表面在动，实际进展不快。');
  text = stripGuidanceParentheticals(text, keywordRegex, sanitizeEnabled);
  text = applyBootstrapLaymanPolish(text, normalizedMode);
  text = normalizeAssistantTextStructure(text);
  if (
    normalizedMode === 'intake'
    && /(眉宇|看得出|你平时应该|你这个人|我心里有数|一看你)/.test(text)
  ) {
    return '我先不急着下判断。先确认两点：你最想问的是哪件事？这件事目前卡在哪一步？';
  }
  if (
    normalizedMode === 'intake'
    && /(日干|时干|值符|值使|天盘|地盘|盘面|这局|局里|落在|落宫|[坎坤震巽中乾兑艮离]\s*[一二三四五六七八九]?\s*宫|[1-9]\s*宫|空亡|击刑|入墓|门迫|局起好了|我先看局)/u.test(text)
  ) {
    return '你先把最想问的核心问题说清楚，我确认后就带你开始起局。';
  }
  return text.trim();
};

const PARAGRAPH_HEADINGS = [
  '结论先行', '断局主线', '象意细化', '应期判断', '行动建议', '风险预警', '环境调整',
  '工位信息', '风险', '突破口', '关键阻力', '核心结论', '依据分析', '环境规划',
  '取用神定宫', '四害溯源', '显性与隐性需求', '隐性需求', '显性需求',
];

const splitTextToSentences = (raw: string): string[] => (
  String(raw || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .split(/(?<=[。！？!?；;])\s*|\n+/)
    .map((item) => item.trim())
    .filter(Boolean)
);

const splitTextToParagraphs = (raw: string): string[] => (
  String(raw || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .split(/\n{2,}/)
    .map((item) => item.trim())
    .filter(Boolean)
);

const splitPostPanByHeadingLines = (raw: string): string[] => {
  const headingPattern = PARAGRAPH_HEADINGS
    .map((item) => escapeRegexLiteral(item))
    .filter(Boolean)
    .join('|');
  if (!headingPattern) return [];
  const headingLineRegex = new RegExp(
    `^(?:#{1,3}\\s*)?(?:\\d+\\s*[)）.、]\\s*)?(?:${headingPattern})(?:\\s*[:：].*)?$`,
    'u',
  );
  const lines = String(raw || '')
    .replace(/\r\n?/g, '\n')
    .split('\n');
  const chunks: string[] = [];
  let current = '';
  let foundHeading = false;
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      if (current && !current.endsWith('\n\n')) current = `${current}\n\n`;
      continue;
    }
    if (headingLineRegex.test(trimmed)) {
      foundHeading = true;
      if (current.trim()) chunks.push(current.trim());
      current = trimmed;
      continue;
    }
    current = current ? `${current}\n${trimmed}` : trimmed;
  }
  if (current.trim()) chunks.push(current.trim());
  if (!foundHeading || chunks.length <= 1) return [];
  return chunks
    .map((item) => item.replace(/\n{3,}/g, '\n\n').trim())
    .filter(Boolean);
};

const chunkBySentence = (sentences: string[], maxChunkChars: number, maxSentencesPerChunk: number): string[] => {
  if (!Array.isArray(sentences) || !sentences.length) return [];
  const chunks: string[] = [];
  let current = '';
  let currentSentenceCount = 0;
  for (const sentence of sentences) {
    if (!current) {
      current = sentence;
      currentSentenceCount = 1;
      continue;
    }
    if ((current + sentence).length <= maxChunkChars && currentSentenceCount < maxSentencesPerChunk) {
      current += sentence;
      currentSentenceCount += 1;
      continue;
    }
    chunks.push(current);
    current = sentence;
    currentSentenceCount = 1;
  }
  if (current) chunks.push(current);
  return chunks.filter(Boolean);
};

const formatChunkWithSpacing = (raw: string, mode: string): string => {
  const normalizedMode = String(mode || '').trim().toLowerCase();
  const text = String(raw || '')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!text) return '';
  if (/\n\n/.test(text)) return text;

  const sentences = splitTextToSentences(text);
  if (sentences.length >= 3) {
    const groupSize = normalizedMode === 'intake' ? 1 : 2;
    const paragraphs: string[] = [];
    for (let i = 0; i < sentences.length; i += groupSize) {
      paragraphs.push(sentences.slice(i, i + groupSize).join(''));
    }
    return paragraphs.join('\n\n');
  }

  if (text.length > 120) {
    const clauses = text
      .split(/(?<=[，,、；;])/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (clauses.length >= 3) {
      const rows: string[] = [];
      let current = '';
      for (const clause of clauses) {
        if (!current) {
          current = clause;
          continue;
        }
        if ((current + clause).length <= 42) {
          current += clause;
          continue;
        }
        rows.push(current);
        current = clause;
      }
      if (current) rows.push(current);
      return rows.join('\n');
    }
  }

  return text;
};

const splitAssistantMessageChunks = (text: string, mode: string): string[] => {
  const raw = String(text || '').trim();
  if (!raw) return [];
  const normalizedMode = String(mode || '').trim().toLowerCase();
  const isPostPanMode = normalizedMode === 'post_pan_bootstrap' || normalizedMode === 'post_pan_followup';
  let chunks: string[] = [];

  if (isPostPanMode) {
    const paragraphChunks = splitTextToParagraphs(raw);
    if (paragraphChunks.length > 1) {
      chunks = paragraphChunks;
    }
    if (!chunks.length) {
      const headingChunks = splitPostPanByHeadingLines(raw);
      if (headingChunks.length > 0) chunks = headingChunks;
    }
    if (!chunks.length) {
      const postPanSentences = splitTextToSentences(raw);
      chunks = postPanSentences.length
        ? chunkBySentence(postPanSentences, 170, 3)
        : [raw];
    }
  } else {
    const maxChunkChars = normalizedMode === 'intake' ? 42 : 100;
    const maxSentencesPerChunk = normalizedMode === 'intake' ? 2 : 3;
    const sentences = splitTextToSentences(raw);
    chunks = sentences.length
      ? chunkBySentence(sentences, maxChunkChars, maxSentencesPerChunk)
      : [raw];
  }

  const normalizedChunks = chunks
    .map((item) => formatChunkWithSpacing(item, normalizedMode))
    .filter(Boolean);
  return normalizedChunks.length ? normalizedChunks : [formatChunkWithSpacing(raw, normalizedMode)];
};

const WenceApp: React.FC<WenceAppProps> = ({
  onBack,
  onOpenPan,
  appLabel = '问测',
  daoName = '轩辕老师',
  featureId = 'wence',
  storageNamespace = 'wence',
}) => {
  const stateStorageKey = useMemo(() => buildWenceStateStorageKey(storageNamespace), [storageNamespace]);
  const historyStorageKey = useMemo(() => buildWenceHistoryStorageKey(storageNamespace), [storageNamespace]);
  const restoredState = useMemo(() => loadPersistedState(stateStorageKey), [stateStorageKey]);
  const launchedFromPan = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('from') === 'pan';
  }, []);
  const launchPanRecordId = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const params = new URLSearchParams(window.location.search);
    return String(params.get('panRecordId') || '').trim();
  }, []);

  const [messages, setMessages] = useState<Message[]>(() => (
    restoredState ? deserializeMessages(restoredState.messages) : []
  ));
  const [inputValue, setInputValue] = useState(restoredState?.inputValue || '');
  const [isTyping, setIsTyping] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(restoredState?.isFirstMessage ?? true);
  const [headerTitle, setHeaderTitle] = useState(daoName);
  const [user, setUser] = useState<StoredUser | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>(() => {
    const restored = restoredState?.suggestions || [];
    return restored.length >= PRESET_VISIBLE_COUNT ? restored.slice(0, PRESET_VISIBLE_COUNT) : pickSuggestions();
  });
  const [usedPanRecordId, setUsedPanRecordId] = useState<string | null>(restoredState?.usedPanRecordId || null);
  const [bootstrappedPanRecordId, setBootstrappedPanRecordId] = useState<string | null>(restoredState?.bootstrappedPanRecordId || null);
  const [panSummary, setPanSummary] = useState<string>(restoredState?.panSummary || '');
  const [isRefreshingSuggestions, setIsRefreshingSuggestions] = useState(false);
  const [panCtaReady, setPanCtaReady] = useState(restoredState?.panCtaReady === true);
  const [latestDebug, setLatestDebug] = useState<WenceDebugInfo | null>(restoredState?.latestDebug || null);
  const [debugHistory, setDebugHistory] = useState<WenceDebugHistoryItem[]>(restoredState?.debugHistory || []);
  const [debugPageOpen, setDebugPageOpen] = useState(false);
  const [clientLogSubmitting, setClientLogSubmitting] = useState(false);
  const [clientLogSubmitResult, setClientLogSubmitResult] = useState<{ ok: boolean; msg: string; logId?: string } | null>(null);
  const [historyPageOpen, setHistoryPageOpen] = useState(false);
  const [coreQuestionModalOpen, setCoreQuestionModalOpen] = useState(false);
  const [coreQuestionDraft, setCoreQuestionDraft] = useState('');
  const [isPanGenerating, setIsPanGenerating] = useState(false);
  const [autoSyncPanContext, setAutoSyncPanContext] = useState(launchedFromPan);
  const [historyItems, setHistoryItems] = useState<PersistedWenceHistoryItem[]>(() => loadPersistedWenceHistory(historyStorageKey));
  const [wenceSanitizeConfig, setWenceSanitizeConfig] = useState<WenceUiSanitizeConfig>({
    bracketSanitizeEnabled: true,
    bracketGuidanceKeywords: DEFAULT_BRACKET_GUIDANCE_KEYWORDS,
    dualLayer: true,
    source: 'ui_default',
  });

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const coreQuestionInputRef = useRef<HTMLTextAreaElement>(null);
  const lastMicTapAtRef = useRef(0);
  const activeRequestIdRef = useRef('');

  useEffect(() => {
    const storedUser = getStoredUser();
    setUser(storedUser || null);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const cfg = await getFeatureChatConfig(featureId);
        if (cancelled || !cfg?.wenceSanitizeConfig) return;
        setWenceSanitizeConfig({
          bracketSanitizeEnabled: cfg.wenceSanitizeConfig.bracketSanitizeEnabled !== false,
          bracketGuidanceKeywords: String(cfg.wenceSanitizeConfig.bracketGuidanceKeywords || DEFAULT_BRACKET_GUIDANCE_KEYWORDS),
          dualLayer: cfg.wenceSanitizeConfig.dualLayer === true,
          source: String(cfg.wenceSanitizeConfig.source || 'feature_config'),
        });
      } catch {
        // noop
      }
    };
    if (featureId === 'wence' || featureId === 'xiaoyaoWence') {
      void run();
    }
    return () => {
      cancelled = true;
    };
  }, [featureId]);

  useEffect(() => {
    if (!coreQuestionModalOpen) return;
    const timer = window.setTimeout(() => {
      coreQuestionInputRef.current?.focus();
      coreQuestionInputRef.current?.setSelectionRange(
        coreQuestionInputRef.current.value.length,
        coreQuestionInputRef.current.value.length,
      );
    }, 16);
    return () => window.clearTimeout(timer);
  }, [coreQuestionModalOpen]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const syncPanContext = useCallback((announce: boolean) => {
    if (!autoSyncPanContext) return;
    const latest = launchPanRecordId
      ? loadPanRecords().find((item) => item.id === launchPanRecordId) || null
      : getLatestPanRecord();
    if (!latest) return;
    if (!launchPanRecordId) {
      const ageMs = Math.max(0, Date.now() - Number(latest.createdAt || 0));
      if (ageMs > 3 * 60 * 1000) return;
    }

    const summary = buildPanSummary(latest);
    if (latest.id === usedPanRecordId) {
      if (!panSummary) setPanSummary(summary);
      return;
    }
    if (!announce) return;

    setUsedPanRecordId(latest.id);
    setBootstrappedPanRecordId(null);
    setPanSummary(summary);
    setPanCtaReady(false);

    const announcementId = `pan-bridge-${latest.id}`;
    setMessages((prev) => {
      if (prev.some((item) => item.id === announcementId)) return prev;
      return [
        ...prev,
        {
          id: announcementId,
          role: 'assistant',
          content: `我已收到你的排盘信息：${summary}\n现在可以继续问，我会按这个盘面给你拆解。`,
          timestamp: new Date(),
          status: 'normal',
          panRecordId: latest.id,
        },
      ];
    });
    setIsFirstMessage(false);
  }, [autoSyncPanContext, panSummary, usedPanRecordId, launchPanRecordId]);

  useEffect(() => {
    syncPanContext(launchedFromPan);
  }, [launchedFromPan, syncPanContext]);

  useEffect(() => {
    try {
      const payload: PersistedWenceState = {
        messages: serializeMessages(messages.filter((item) => item.status !== 'streaming')),
        inputValue,
        isFirstMessage,
        suggestions,
        usedPanRecordId,
        bootstrappedPanRecordId,
        panSummary,
        panCtaReady,
        latestDebug,
        debugHistory,
        autoSyncPanContext,
      };
      persistWenceState(stateStorageKey, payload);
    } catch {
      // noop
    }
  }, [messages, inputValue, isFirstMessage, suggestions, usedPanRecordId, bootstrappedPanRecordId, panSummary, panCtaReady, latestDebug, debugHistory, autoSyncPanContext, stateStorageKey]);

  const buildCurrentPersistedState = useCallback((): PersistedWenceState => ({
    messages: serializeMessages(messages.filter((item) => item.status !== 'streaming')),
    inputValue,
    isFirstMessage,
    suggestions,
    usedPanRecordId,
    bootstrappedPanRecordId,
    panSummary,
    panCtaReady,
    latestDebug,
    debugHistory,
    autoSyncPanContext,
  }), [
    autoSyncPanContext,
    bootstrappedPanRecordId,
    debugHistory,
    inputValue,
    isFirstMessage,
    latestDebug,
    messages,
    panCtaReady,
    panSummary,
    suggestions,
    usedPanRecordId,
  ]);

  const archiveCurrentConversation = useCallback(() => {
    const cleanMessages = messages.filter((item) => item.status !== 'streaming');
    if (!cleanMessages.length) return;
    const lastUserText = [...cleanMessages].reverse().find((item) => item.role === 'user')?.content || '';
    const title = String(lastUserText || cleanMessages[0]?.content || '未命名会话').trim().slice(0, 36) || '未命名会话';
    const nowIso = new Date().toISOString();
    const snapshot: PersistedWenceHistoryItem = {
      id: `hist-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      title,
      createdAt: nowIso,
      updatedAt: nowIso,
      state: buildCurrentPersistedState(),
    };
    setHistoryItems((prev) => {
      const next = [snapshot, ...prev].slice(0, 40);
      savePersistedWenceHistory(historyStorageKey, next);
      return next;
    });
  }, [buildCurrentPersistedState, historyStorageKey, messages]);

  const restoreConversationFromHistory = useCallback((item: PersistedWenceHistoryItem) => {
    const state = item.state;
    setMessages(deserializeMessages(Array.isArray(state.messages) ? state.messages : []));
    setInputValue(String(state.inputValue || ''));
    setIsFirstMessage(state.isFirstMessage !== false);
    setSuggestions(Array.isArray(state.suggestions) && state.suggestions.length >= PRESET_VISIBLE_COUNT
      ? state.suggestions.slice(0, PRESET_VISIBLE_COUNT)
      : pickSuggestions());
    setUsedPanRecordId(typeof state.usedPanRecordId === 'string' && state.usedPanRecordId ? state.usedPanRecordId : null);
    setBootstrappedPanRecordId(typeof state.bootstrappedPanRecordId === 'string' && state.bootstrappedPanRecordId ? state.bootstrappedPanRecordId : null);
    setPanSummary(String(state.panSummary || ''));
    setPanCtaReady(state.panCtaReady === true);
    setLatestDebug(state.latestDebug && typeof state.latestDebug === 'object' ? state.latestDebug : null);
    setDebugHistory(Array.isArray(state.debugHistory) ? state.debugHistory.slice(0, 120) : []);
    setAutoSyncPanContext(false);
    setIsTyping(false);
    setHeaderTitle(daoName);
    setDebugPageOpen(false);
    setHistoryPageOpen(false);
  }, [daoName]);

  const deleteHistoryItem = useCallback((id: string) => {
    setHistoryItems((prev) => {
      const next = prev.filter((item) => item.id !== id);
      savePersistedWenceHistory(historyStorageKey, next);
      return next;
    });
  }, [historyStorageKey]);

  const clearAllHistory = useCallback(() => {
    setHistoryItems([]);
    savePersistedWenceHistory(historyStorageKey, []);
  }, [historyStorageKey]);

  const buildCurrentDebugPayload = useCallback(() => {
    const cleanMessages = messages
      .filter((item) => item.status !== 'streaming')
      .map((item) => ({
        id: item.id,
        role: item.role,
        content: item.content,
        timestamp: item.timestamp.toISOString(),
        panRecordId: item.panRecordId || '',
      }));
    const currentPanRecord = usedPanRecordId
      ? loadPanRecords().find((item) => item.id === usedPanRecordId) || null
      : null;
    return {
      exportedAt: new Date().toISOString(),
      appLabel,
      storageNamespace,
      panSummary,
      usedPanRecordId,
      bootstrappedPanRecordId,
      latestDebug,
      debugHistory,
      messages: cleanMessages,
      currentPan: currentPanRecord
        ? {
          id: currentPanRecord.id,
          summary: buildPanSummary(currentPanRecord),
          sourceDateTime: currentPanRecord.sourceDateTime,
          panMode: currentPanRecord.panMode,
        }
        : null,
      uiState: {
        autoSyncPanContext,
        isFirstMessage,
        panCtaReady,
        historyCount: historyItems.length,
      },
    };
  }, [
    appLabel,
    autoSyncPanContext,
    bootstrappedPanRecordId,
    debugHistory,
    historyItems.length,
    isFirstMessage,
    latestDebug,
    messages,
    panCtaReady,
    panSummary,
    storageNamespace,
    usedPanRecordId,
  ]);

  const handleSubmitCurrentDebugLog = useCallback(async () => {
    setClientLogSubmitting(true);
    setClientLogSubmitResult(null);
    try {
      const result = await submitClientLog({
        source: `${storageNamespace || 'wence'}-app`,
        category: 'wence-debug',
        appLabel,
        message: `${appLabel} 调试日志`,
        payload: buildCurrentDebugPayload(),
      });
      setClientLogSubmitResult({
        ok: true,
        msg: result.truncated
          ? '调试日志已提交，内容过长部分已自动截断，请记下这个 Log 代号。'
          : '调试日志已提交，请记下这个 Log 代号。',
        logId: result.logId,
      });
    } catch (error) {
      setClientLogSubmitResult({
        ok: false,
        msg: error instanceof Error ? error.message : '提交日志失败，请稍后重试。',
      });
    } finally {
      setClientLogSubmitting(false);
    }
  }, [appLabel, buildCurrentDebugPayload, storageNamespace]);

  const dismissClientLogResult = useCallback(() => {
    setClientLogSubmitResult(null);
  }, []);

  const buildChatMessages = (
    history: Message[],
    currentMode: string,
    preferredPanRecordId: string | null,
  ) => {
    if (currentMode === 'post_pan_followup') {
      const panRecord = resolvePanRecordFromHistory(history, preferredPanRecordId);
      const recentTurns = mergeHistoryTurns(
        history.filter((item) => !isPanBridgeMessage(item)),
      ).slice(-FOLLOWUP_HISTORY_TURN_LIMIT);
      const apiMessages: Array<{ role: 'user' | 'assistant'; content: string }> = [];
      if (panRecord) {
        apiMessages.push({
          role: 'assistant',
          content: `以下是本局固定盘面，请始终按这个盘继续回答，不要脱离原局。\n\n${buildPanContextForApi(panRecord)}`,
        });
      }
      return [...apiMessages, ...recentTurns];
    }

    const apiMessages: Array<{ role: 'user' | 'assistant'; content: string }> = [];
    const appendedPanRecordIds = new Set<string>();
    history
      .filter((item) => item.role === 'user' || item.role === 'assistant')
      .forEach((item) => {
        let content = String(item.content || '');
        if (item.panRecordId && !appendedPanRecordIds.has(item.panRecordId)) {
          const record = loadPanRecords().find((entry) => entry.id === item.panRecordId);
          if (record) {
            content = `${content}\n\n${buildPanContextForApi(record)}`.trim();
            appendedPanRecordIds.add(item.panRecordId);
          }
        }
        apiMessages.push({ role: item.role, content });
      });

    return apiMessages;
  };

  const resolveCurrentMode = (summaryText: string, panId: string | null, bootstrappedId: string | null): string => (
    !summaryText
      ? 'intake'
      : (panId && bootstrappedId !== panId ? 'post_pan_bootstrap' : 'post_pan_followup')
  );

  const replaceStreamingWithChunk = (streamingId: string, chunkText: string) => {
    setMessages((prev) => {
      let replaced = false;
      const next = prev.map((msg) => {
        if (msg.id !== streamingId) return msg;
        replaced = true;
        return { ...msg, content: chunkText, status: 'normal' };
      });
      if (!replaced) {
        next.push({
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: chunkText,
          timestamp: new Date(),
          status: 'normal',
        });
      }
      return next;
    });
  };

  const updateStreamingMessageContent = (streamingId: string, chunkText: string, finalize = false) => {
    setMessages((prev) => {
      let replaced = false;
      const next = prev.map((msg) => {
        if (msg.id !== streamingId) return msg;
        replaced = true;
        return {
          ...msg,
          content: chunkText,
          status: finalize ? 'normal' : 'streaming',
        };
      });
      if (!replaced) {
        next.push({
          id: finalize ? `assistant-${Date.now()}` : streamingId,
          role: 'assistant',
          content: chunkText,
          timestamp: new Date(),
          status: finalize ? 'normal' : 'streaming',
        });
      }
      return next;
    });
  };

  const appendAssistantChunk = (chunkText: string) => {
    setMessages((prev) => ([
      ...prev,
      {
        id: `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        role: 'assistant',
        content: chunkText,
        timestamp: new Date(),
        status: 'normal',
      },
    ]));
  };

  const deliverAssistantChunks = async (
    streamingId: string,
    chunks: string[],
    fallbackText: string,
    requestId = '',
  ) => {
    const isActive = () => !requestId || activeRequestIdRef.current === requestId;
    if (!isActive()) return;
    const first = chunks[0] || fallbackText;
    replaceStreamingWithChunk(streamingId, first);
    const rest = chunks.slice(1);
    for (const extra of rest) {
      await sleep(Math.max(220, Math.min(680, 180 + extra.length * 8)));
      if (!isActive()) return;
      appendAssistantChunk(extra);
    }
  };

  const runWenceReply = async (
    historyForApi: Message[],
    currentMode: string,
    streamingId: string,
    usedPanIdSnapshot: string | null,
    requestId: string,
  ) => {
    const timeoutMs = resolveWenceModeTimeoutMs(currentMode);
    const shouldUseStream = currentMode === 'post_pan_followup';
    const isStaleRequest = () => activeRequestIdRef.current !== requestId;
    const latestUserText = [...historyForApi]
      .reverse()
      .find((item) => item.role === 'user')
      ?.content || '';
    const startedAt = Date.now();
    let responseMode: 'stream' | 'non_stream' = 'non_stream';
    let streamedRawText = '';
    const response = await chatApiStream(
      {
        messages: buildChatMessages(historyForApi, currentMode, usedPanIdSnapshot),
        feature: featureId,
        featureMode: currentMode,
        stream: shouldUseStream,
        timeoutMs,
      },
      {
        onMode: (mode) => {
          responseMode = mode;
        },
        onChunk: (chunk) => {
          if (!shouldUseStream || isStaleRequest()) return;
          streamedRawText += String(chunk || '');
          const previewText = sanitizeAssistantTextForUi(streamedRawText, currentMode, wenceSanitizeConfig) || streamedRawText.trim();
          if (previewText) {
            updateStreamingMessageContent(streamingId, previewText, false);
          }
        },
      },
    );
    if (isStaleRequest()) return;

    const debug = parseWenceDebugFromResponse(response);
    if (debug) {
      setLatestDebug(debug);
      setDebugHistory((prev) => ([
        {
          id: `dbg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          ts: new Date().toISOString(),
          mode: currentMode,
          userText: String(latestUserText),
          assistantPreview: String(response.text || '').trim(),
          durationMs: debug.durationMs,
          knowledgeRefCount: Array.isArray(debug.knowledgeRefs) ? debug.knowledgeRefs.length : 0,
          promptPolicy: debug.promptPolicy || '',
          finishReason: debug.finishReason || '',
          tokenUsage: debug.tokenUsage,
        },
        ...prev,
      ].slice(0, 120)));
    } else if (shouldUseStream) {
      const fallbackDebug: WenceDebugInfo = {
        modelText: latestDebug?.modelText || '',
        featurePromptPreview: '',
        thinkingSystemPromptPreview: '',
        thinkingUserPromptPreview: '',
        chainPromptPreview: '',
        chainPromptChars: null,
        chainPromptSectionCount: null,
        promptMode: '',
        thinkingResult: null,
        knowledgeRefs: [],
        knowledgeQueryUsed: '',
        durationMs: Date.now() - startedAt,
        promptPolicy: 'followup_stream',
        promptPolicyReason: 'client_stream_no_meta',
        featurePromptChars: null,
        finishReason: 'stop',
        tokenUsage: {
          promptTokens: null,
          completionTokens: null,
          reasoningTokens: null,
          totalTokens: null,
        },
        sanitizeConfig: wenceSanitizeConfig,
        outputConfig: {
          featureMode: currentMode,
          streamUsed: true,
          jsonMode: false,
        },
      };
      setLatestDebug(fallbackDebug);
      setDebugHistory((prev) => ([
        {
          id: `dbg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          ts: new Date().toISOString(),
          mode: currentMode,
          userText: String(latestUserText),
          assistantPreview: String(response.text || streamedRawText || '').trim(),
          durationMs: fallbackDebug.durationMs,
          knowledgeRefCount: 0,
          promptPolicy: fallbackDebug.promptPolicy || '',
          finishReason: fallbackDebug.finishReason || '',
          tokenUsage: fallbackDebug.tokenUsage,
        },
        ...prev,
      ].slice(0, 120)));
    }

    const finalText = sanitizeAssistantTextForUi(
      String(response.text || streamedRawText || '').trim() || '我先记下了，你再补一句最核心的问题。',
      currentMode,
      wenceSanitizeConfig,
    );
    const fallbackReplyText = finalText || '我先记下了，你再补一句最核心的问题。';
    const chunks = splitAssistantMessageChunks(fallbackReplyText, currentMode);
    await deliverAssistantChunks(streamingId, chunks, fallbackReplyText, requestId);
    if (isStaleRequest()) return;

    if (currentMode === 'intake') {
      if (hasPanCtaCue(finalText) && !hasPanCtaNegativeCue(finalText)) {
        setPanCtaReady(true);
      }
    } else {
      setPanCtaReady(false);
    }
    if (currentMode === 'post_pan_bootstrap' && usedPanIdSnapshot) {
      setBootstrappedPanRecordId(usedPanIdSnapshot);
    }
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || isTyping) return;

    const now = Date.now();
    const userMessage: Message = {
      id: `user-${now}`,
      role: 'user',
      content: text,
      timestamp: new Date(),
      status: 'normal',
    };
    const streamingId = `streaming-${now}-${Math.random().toString(36).slice(2, 7)}`;
    const requestId = `req-${now}-${Math.random().toString(36).slice(2, 8)}`;
    activeRequestIdRef.current = requestId;

    const historyForApi = [
      ...messages.filter((item) => item.status !== 'streaming'),
      userMessage,
    ];
    const currentMode = resolveCurrentMode(panSummary, usedPanRecordId, bootstrappedPanRecordId);

    setMessages((prev) => [
      ...prev.filter((item) => item.status !== 'streaming'),
      userMessage,
      {
        id: streamingId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        status: 'streaming',
      },
    ]);
    setInputValue('');
    setIsFirstMessage(false);
    setIsTyping(true);
    setHeaderTitle(currentMode === 'post_pan_bootstrap' ? '正在思考中...' : '对方正在输入...');

    try {
      await runWenceReply(historyForApi, currentMode, streamingId, usedPanRecordId, requestId);
    } catch (error) {
      if (activeRequestIdRef.current !== requestId) return;
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      const latestUserText = [...historyForApi]
        .reverse()
        .find((item) => item.role === 'user')
        ?.content || '';
      setDebugHistory((prev) => ([
        {
          id: `dbg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          ts: new Date().toISOString(),
          mode: currentMode,
          userText: String(latestUserText),
          assistantPreview: `[ERROR] ${errorMessage}`,
          durationMs: null,
          knowledgeRefCount: 0,
          promptPolicy: '',
          finishReason: 'error',
          tokenUsage: {
            promptTokens: null,
            completionTokens: null,
            reasoningTokens: null,
            totalTokens: null,
          },
        },
        ...prev,
      ].slice(0, 120)));
      setMessages((prev) => {
        const streamingMsg = prev.find((m) => m.id === streamingId);
        const partialContent = streamingMsg?.content?.trim() || '';
        const next = prev.filter((item) => item.id !== streamingId);
        if (partialContent) {
          next.push({
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: `${partialContent}\n\n（回复未完整，连接中断。可重新发送再试。）`,
            timestamp: new Date(),
            status: 'normal',
          });
          return next;
        }
        return [
          ...next,
          {
            id: `error-${Date.now()}`,
            role: 'assistant',
            content: `抱歉，当前响应失败：${errorMessage}`,
            timestamp: new Date(),
            status: 'normal',
          },
        ];
      });
    } finally {
      if (activeRequestIdRef.current === requestId) {
        activeRequestIdRef.current = '';
        setIsTyping(false);
        setHeaderTitle(daoName);
      }
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const handleRefreshSuggestions = () => {
    setIsRefreshingSuggestions(true);
    setSuggestions((prev) => pickSuggestions(prev));
    window.setTimeout(() => {
      setIsRefreshingSuggestions(false);
    }, 260);
  };

  const handleResetConversation = () => {
    archiveCurrentConversation();
    activeRequestIdRef.current = '';
    setMessages([]);
    setInputValue('');
    setIsTyping(false);
    setIsFirstMessage(true);
    setHeaderTitle(daoName);
    setSuggestions(pickSuggestions());
    setUsedPanRecordId(null);
    setBootstrappedPanRecordId(null);
    setPanSummary('');
    setPanCtaReady(false);
    setLatestDebug(null);
    setDebugHistory([]);
    setDebugPageOpen(false);
    setClientLogSubmitting(false);
    setClientLogSubmitResult(null);
    setHistoryPageOpen(false);
    setCoreQuestionModalOpen(false);
    setCoreQuestionDraft('');
    setIsPanGenerating(false);
    setAutoSyncPanContext(false);
    clearPersistedWenceState(stateStorageKey);
  };

  const handleVoiceButtonTap = useCallback(() => {
    const now = Date.now();
    if (now - lastMicTapAtRef.current <= 320) {
      setClientLogSubmitResult(null);
      setDebugPageOpen(true);
      lastMicTapAtRef.current = 0;
      return;
    }
    lastMicTapAtRef.current = now;
  }, []);

  const openCoreQuestionModal = () => {
    setCoreQuestionDraft(inputValue.trim());
    setCoreQuestionModalOpen(true);
  };

  const handleConfirmCoreQuestionAndGoPan = async () => {
    const normalized = String(coreQuestionDraft || '').trim();
    if (!normalized) return;
    setCoreQuestionModalOpen(false);
    setCoreQuestionDraft('');
    setInputValue('');
    await handleGoPan(normalized);
  };

  const handleGoPan = async (seedQuestion?: string) => {
    if (isPanGenerating) return;
    const latestQuestion = String(seedQuestion || '').trim()
      || inputValue.trim()
      || [...messages].reverse().find((item) => item.role === 'user')?.content
      || '';
    const normalizedQuestion = String(latestQuestion || '').trim();
    if (!normalizedQuestion) {
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-pan-hint-${Date.now()}`,
          role: 'assistant',
          content: '先用一句话说清你最想问的事项，我再为你起局。',
          timestamp: new Date(),
          status: 'normal',
        },
      ]);
      return;
    }
    const loadingMessageId = `assistant-pan-loading-${Date.now()}`;
    setIsPanGenerating(true);
    setMessages((prev) => [
      ...prev,
      {
        id: loadingMessageId,
        role: 'assistant',
        content: '稍等片刻，我先帮你起局。',
        timestamp: new Date(),
        status: 'normal',
      },
    ]);
    try {
      const now = new Date();
      const dateTimeValue = toDateTimeLocalValue(now);
      const keIndex = Math.max(0, Math.min(5, Math.floor(now.getMinutes() / 10)));
      const response = await calculatePanApi({
        dateTime: dateTimeValue,
        panMode: 'hour',
        question: normalizedQuestion,
        keIndex,
        manualJu: null,
        useTrueSolarTime: false,
        timezoneOffsetMinutes: now.getTimezoneOffset(),
      });
      const serializedPan = response.pan as SavedPanRecord['pan'];
      if (!serializedPan || !Array.isArray((serializedPan as { palaces?: unknown }).palaces)) {
        throw new Error('排盘数据异常');
      }
      const recordId = `wence-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      const record: SavedPanRecord = {
        id: recordId,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        question: normalizedQuestion,
        sourceDateTime: response.sourceDateTime || dateTimeValue,
        panMode: response.mode === 'ke' ? 'ke' : 'hour',
        consultMode: 'remote',
        keIndex: Number.isFinite(Number(response.keIndex)) ? Number(response.keIndex) : keIndex,
        manualJu: 'auto',
        useTrueSolarTime: false,
        trueSolar: null,
        pan: serializedPan,
      };
      const previousRecords = loadPanRecords().filter((item) => item.id !== record.id);
      savePanRecords([record, ...previousRecords]);
      const summary = buildPanSummary(record);
      setUsedPanRecordId(record.id);
      setBootstrappedPanRecordId(null);
      setPanSummary(summary);
      setPanCtaReady(false);
      setLatestDebug(null);
      setAutoSyncPanContext(false);
      const panPreviewMessage: Message = {
        id: `assistant-pan-preview-${record.id}`,
        role: 'assistant',
        content: '局起好了，已自动保存到【档案】和【奇门排盘记录】。请稍等，我先根据你的情况认真思考……',
        timestamp: new Date(),
        status: 'normal',
        panRecordId: record.id,
      };
      const thinkingStreamingId = `assistant-pan-thinking-${Date.now()}`;
      setMessages((prev) => {
        const next = prev.filter((item) => item.id !== loadingMessageId);
        return [
          ...next,
          panPreviewMessage,
          {
            id: thinkingStreamingId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            status: 'streaming',
          },
        ];
      });
      setIsTyping(true);
      setHeaderTitle('正在思考中...');
      const autoRequestId = `req-auto-pan-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      activeRequestIdRef.current = autoRequestId;
      const baseHistory = messages.filter((item) => item.status !== 'streaming');
      const autoUserMessage: Message = {
        id: `user-auto-pan-${Date.now()}`,
        role: 'user',
        content: '我已经起好局了，你先看看这个局。',
        timestamp: new Date(),
        status: 'normal',
        panRecordId: record.id,
      };
      try {
        await runWenceReply(
          [...baseHistory, panPreviewMessage, autoUserMessage],
          'post_pan_bootstrap',
          thinkingStreamingId,
          record.id,
          autoRequestId,
        );
      } catch (autoError) {
        if (activeRequestIdRef.current !== autoRequestId) return;
        const autoErrorMessage = autoError instanceof Error ? autoError.message : '未知错误';
        setDebugHistory((prev) => ([
          {
            id: `dbg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            ts: new Date().toISOString(),
            mode: 'post_pan_bootstrap',
            userText: '我已经起好局了，你先看看这个局。',
            assistantPreview: `[ERROR] ${autoErrorMessage}`,
            durationMs: null,
            knowledgeRefCount: 0,
            promptPolicy: '',
            finishReason: 'error',
            tokenUsage: {
              promptTokens: null,
              completionTokens: null,
              reasoningTokens: null,
              totalTokens: null,
            },
          },
          ...prev,
        ].slice(0, 120)));
        setMessages((prev) => {
          const next = prev.filter((item) => item.id !== thinkingStreamingId);
          return [
            ...next,
            {
              id: `assistant-pan-thinking-error-${Date.now()}`,
              role: 'assistant',
              content: `首轮解盘失败：${autoErrorMessage}`,
              timestamp: new Date(),
              status: 'normal',
            },
          ];
        });
      } finally {
        if (activeRequestIdRef.current === autoRequestId) {
          activeRequestIdRef.current = '';
          setIsTyping(false);
          setHeaderTitle(daoName);
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '未知错误';
      setMessages((prev) => {
        const next = prev.filter((item) => item.id !== loadingMessageId);
        return [
          ...next,
          {
            id: `assistant-pan-error-${Date.now()}`,
            role: 'assistant',
            content: `排盘失败：${errorMessage}。请稍后重试。`,
            timestamp: new Date(),
            status: 'normal',
          },
        ];
      });
      if (onOpenPan) {
        setMessages((prev) => [
          ...prev,
          {
            id: `assistant-pan-fallback-${Date.now()}`,
            role: 'assistant',
            content: '如果你愿意，也可以先进入完整排盘页手动起局。',
            timestamp: new Date(),
            status: 'normal',
          },
        ]);
      }
    } finally {
      setIsPanGenerating(false);
    }
  };

  const formatTime = (date: Date) => (
    date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  );

  const shouldShowPanCta = panCtaReady && !panSummary;
  return (
    <div className="wence-app-container">
      <style>{`
        .wence-app-container {
          width: 100%;
          height: 100dvh;
          min-height: 100dvh;
          max-width: 414px;
          margin: 0 auto;
          background-color: #ffffff;
          display: flex;
          flex-direction: column;
          position: relative;
          overflow: hidden;
        }

        .wence-header {
          display: flex;
          flex: 0 0 56px;
          align-items: center;
          height: 56px;
          min-height: 56px;
          padding: 0 20px;
          background: rgba(255, 255, 255, 0.96);
          z-index: 20;
          border-bottom: 1px solid #f0f0f0;
          backdrop-filter: blur(12px);
        }

        .wence-back-btn {
          font-size: 24px;
          cursor: pointer;
          color: #333;
          z-index: 11;
          background: none;
          border: none;
          padding: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .wence-header-actions {
          margin-left: auto;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          z-index: 12;
        }

        .wence-icon-btn {
          border: none;
          background: transparent;
          color: #7f7f7f;
          border-radius: 999px;
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .wence-icon-btn:hover {
          background: #f3f3f3;
          color: #5f5f5f;
        }

        .wence-icon-btn:active {
          transform: translateY(1px);
          background: #ececec;
        }

        .wence-icon-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
          transform: none;
          background: transparent;
        }

        .wence-title {
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          font-size: 17px;
          font-weight: 500;
          color: #333;
          white-space: nowrap;
          transition: all 0.3s ease;
        }

        .wence-main-content {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 20px;
          padding-bottom: 154px;
          scroll-behavior: smooth;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
        }

        .wence-initial-state {
          animation: fadeIn 0.5s ease;
        }

        .wence-profile-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          margin-top: 10px;
          margin-bottom: 30px;
        }

        .wence-avatar-container {
          width: 110px;
          height: 110px;
          border-radius: 50%;
          box-shadow: 0 0 30px rgba(162, 205, 255, 0.6);
          margin-bottom: 20px;
          overflow: hidden;
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: #e6f0fa;
        }

        .wence-avatar-container img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .wence-disclaimer {
          font-size: 12px;
          color: #999;
          margin-bottom: 15px;
        }

        .wence-greeting {
          font-size: 17px;
          color: #000;
          font-weight: 500;
          text-align: center;
          line-height: 1.5;
          padding: 0 10px;
        }

        .wence-suggestions-section {
          margin-top: 12px;
        }

        .wence-suggestions-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding: 0 5px;
        }

        .wence-suggestions-header span {
          font-size: 14px;
          color: #999;
        }

        .wence-refresh-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #999;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .wence-refresh-btn.is-spinning svg {
          animation: spinOnce 0.26s linear;
        }

        .wence-suggestion-btn {
          display: flex;
          justify-content: space-between;
          align-items: center;
          width: 100%;
          padding: 14px 18px;
          border: 1px solid #f0f0f0;
          border-radius: 24px;
          background-color: #fff;
          margin-bottom: 10px;
          font-size: 15px;
          color: #000;
          cursor: pointer;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
          transition: background-color 0.2s;
          text-align: left;
        }

        .wence-suggestion-btn:active {
          background-color: #f9f9f9;
        }

        .wence-chat-state {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }

        .wence-message-row {
          display: flex;
          width: 100%;
          align-items: flex-start;
          gap: 10px;
        }

        .wence-message-row.wence-bot {
          justify-content: flex-start;
        }

        .wence-message-row.wence-user {
          justify-content: flex-end;
        }

        .wence-chat-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          object-fit: cover;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .wence-message-content {
          max-width: min(86%, 360px);
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .wence-user .wence-message-content {
          align-items: flex-end;
        }

        .wence-bot .wence-message-content {
          align-items: flex-start;
        }

        .wence-bubble {
          display: inline-block;
          width: fit-content;
          max-width: 100%;
          min-width: 50px;
          padding: 12px 14px;
          border-radius: 12px;
          font-size: 15px;
          line-height: 1.6;
          white-space: pre-wrap;
          overflow-wrap: anywhere;
          word-break: break-word;
          writing-mode: horizontal-tb;
          text-orientation: mixed;
          position: relative;
        }

        .wence-bot .wence-bubble {
          background-color: #f4f4f4;
          color: #333;
          border-top-left-radius: 4px;
        }

        .wence-user .wence-bubble {
          background-color: #cce5ff;
          color: #000;
          border-top-right-radius: 4px;
        }

        .wence-bot .wence-bubble.wence-bubble-pan {
          max-width: 100%;
          width: 100%;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          padding: 10px;
          border-top-left-radius: 8px;
        }

        .wence-message-meta {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 6px;
          padding-right: 2px;
          min-height: 18px;
        }

        .wence-message-time {
          font-size: 11px;
          color: #999;
          line-height: 1.1;
        }

        .wence-message-meta-btn {
          width: 20px;
          height: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          border-radius: 999px;
          background: transparent;
          color: #9ca3af;
          cursor: pointer;
          padding: 0;
        }

        .wence-message-meta-btn:hover {
          background: #f3f4f6;
          color: #6b7280;
        }

        .wence-message-meta-btn:disabled {
          opacity: 0.42;
          cursor: not-allowed;
          background: transparent;
        }

        .wence-pan-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: none;
          border-radius: 999px;
          padding: 9px 14px;
          background: linear-gradient(90deg, #7f5a35, #a87a48);
          color: #fff;
          font-size: 13px;
          cursor: pointer;
          box-shadow: 0 5px 14px rgba(131, 90, 47, 0.24);
        }

        .wence-pan-btn:active {
          transform: translateY(1px);
        }

        .wence-pan-btn:disabled {
          cursor: not-allowed;
          opacity: 0.65;
          box-shadow: none;
        }
        .wence-pan-cta-inline {
          margin-top: 10px;
          display: inline-flex;
          width: 100%;
          flex-direction: row;
          align-items: center;
          justify-content: flex-start;
        }
        .wence-pan-btn-inline {
          margin-top: 2px;
        }
        .wence-bubble-text {
          margin-top: 8px;
        }
        .wence-pan-card {
          width: 100%;
          border: 1px solid #dbeafe;
          border-radius: 12px;
          background: #ffffff;
          overflow: hidden;
        }
        .wence-pan-summary-grid {
          display: grid;
          grid-template-columns: 54px 1fr;
          border-bottom: 1px solid #e2e8f0;
        }
        .wence-pan-summary-label {
          background: #fefbec;
          color: #9c5b31;
          font-size: 11px;
          font-weight: 700;
          border-right: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 4px 6px;
        }
        .wence-pan-summary-value {
          font-size: 12px;
          color: #0f172a;
          border-bottom: 1px solid #e2e8f0;
          padding: 4px 8px;
          text-align: left;
        }
        .wence-pan-top5 {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          border-bottom: 1px solid #e2e8f0;
        }
        .wence-pan-top5 > div {
          border-right: 1px solid #e2e8f0;
          padding: 5px 4px;
          text-align: center;
        }
        .wence-pan-top5 > div:last-child { border-right: none; }
        .wence-pan-top5 span {
          display: block;
          font-size: 11px;
          color: #8c7b64;
        }
        .wence-pan-top5 strong {
          display: block;
          margin-top: 1px;
          font-size: 12px;
          color: #1f2937;
        }
        .wence-pan-pillars {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          border-bottom: 1px solid #e2e8f0;
        }
        .wence-pan-pillars.five { grid-template-columns: repeat(5, 1fr); }
        .wence-pan-pillar-item {
          border-right: 1px solid #e2e8f0;
          text-align: center;
          padding: 5px 4px;
        }
        .wence-pan-pillar-item:last-child { border-right: none; }
        .wence-pan-pillar-item span {
          display: block;
          font-size: 11px;
          color: #9c5b31;
        }
        .wence-pan-pillar-item strong {
          display: block;
          margin-top: 1px;
          font-size: 12px;
          color: #0f172a;
        }
        .wence-pan-board {
          position: relative;
          margin: 16px auto 6px auto;
          width: min(272px, 100%);
          padding: 16px 22px 20px 22px;
          overflow: visible;
        }
        .wence-pan-grid-inner {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: repeat(3, 1fr);
          border: 1px solid #0f172a;
          background: #fff;
        }
        .wence-pan-cell {
          border: 1px solid #0f172a;
          aspect-ratio: 1 / 1;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #fff;
        }
        .wence-pan-cell-center { background: #f8fafc; }
        .wence-pan-void {
          position: absolute;
          top: 2px;
          right: 4px;
          font-size: 11px;
          color: #64748b;
        }
        .wence-pan-deity {
          position: absolute;
          top: 3px;
          left: 4px;
          font-size: 12px;
          color: #0f172a;
          font-weight: 500;
        }
        .wence-pan-mid {
          position: absolute;
          left: 4px;
          right: 4px;
          top: 50%;
          transform: translateY(-54%);
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 12px;
          line-height: 1;
        }
        .wence-pan-heaven { display: inline-flex; gap: 1px; }
        .wence-pan-star { color: #0f172a; font-size: 12px; }
        .wence-pan-earth {
          position: absolute;
          left: 4px;
          bottom: 3px;
          display: inline-flex;
          gap: 1px;
          font-size: 12px;
          line-height: 1;
        }
        .wence-pan-gate {
          position: absolute;
          right: 4px;
          bottom: 3px;
          font-size: 12px;
          color: #0f172a;
          line-height: 1;
        }
        .wence-pan-stem-default { color: #0f172a; }
        .wence-pan-stem-mu { color: #b45309; }
        .wence-pan-stem-xing { color: #2563eb; }
        .wence-pan-stem-xingmu { color: #0284c7; }
        .wence-pan-gate-menpo { color: #dc2626 !important; }
        .wence-pan-outer-stem {
          position: absolute;
          font-size: 12px;
          color: #0f172a;
          line-height: 1;
          font-weight: 500;
        }
        .wence-pan-outer-inner {
          display: inline-block;
          min-width: 1.6em;
        }
        .wence-pan-outer-inner-left {
          text-align: right;
          transform: translateX(-74%);
        }
        .wence-pan-outer-inner-right {
          text-align: left;
          transform: translateX(74%);
        }
        .wence-pan-outer-inner-center {
          text-align: center;
        }
        .wence-pan-outer-pos9 { left: 50%; top: 8px; transform: translate(-50%, -58%); }
        .wence-pan-outer-pos4 { left: 18px; top: 16.67%; transform: translate(-46%, -50%); }
        .wence-pan-outer-pos3 { left: 18px; top: 50%; transform: translate(-46%, -50%); }
        .wence-pan-outer-pos8 { left: 18px; top: 83.33%; transform: translate(-46%, -50%); }
        .wence-pan-outer-pos2 { right: 18px; top: 16.67%; transform: translate(46%, -50%); }
        .wence-pan-outer-pos7 { right: 18px; top: 50%; transform: translate(46%, -50%); }
        .wence-pan-outer-pos6 { right: 18px; top: 83.33%; transform: translate(46%, -50%); }
        .wence-pan-outer-pos1 { left: 50%; bottom: 8px; transform: translate(-50%, 58%); }
        .wence-pan-horse {
          position: absolute;
          font-size: 12px;
          color: #dc2626;
          font-weight: 600;
          line-height: 1;
        }
        .wence-pan-horse-inner {
          display: inline-block;
          min-width: 1.3em;
          text-align: center;
        }
        .wence-pan-horse.wence-pan-horse-corner4 {
          left: 18px;
          top: 8px;
          transform: translate(-78%, -58%);
        }
        .wence-pan-horse.wence-pan-horse-corner2 {
          right: 18px;
          top: 8px;
          transform: translate(78%, -58%);
        }
        .wence-pan-horse.wence-pan-horse-corner8 {
          left: 18px;
          bottom: 8px;
          transform: translate(-78%, 58%);
        }
        .wence-pan-horse.wence-pan-horse-corner6 {
          right: 18px;
          bottom: 8px;
          transform: translate(78%, 58%);
        }
        .wence-pan-legend {
          padding: 0 10px 8px 10px;
          font-size: 11px;
          color: #64748b;
          text-align: left;
        }

        .wence-debug-page {
          position: fixed;
          inset: 0;
          z-index: 1300;
          background: #f8fafc;
          display: flex;
          flex-direction: column;
        }
        .wence-debug-page-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
          min-height: 56px;
          padding: 0 14px;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
        }
        .wence-debug-page-head strong {
          font-size: 15px;
          color: #0f172a;
        }
        .wence-debug-head-actions {
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .wence-debug-head-btn {
          border: 1px solid #d7deea;
          border-radius: 999px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
          color: #334155;
          padding: 5px 11px;
          font-size: 12px;
          line-height: 1;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(15, 23, 42, 0.08);
        }
        .wence-debug-head-btn-primary {
          border-color: #93c5fd !important;
          background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%) !important;
          color: #1d4ed8 !important;
        }
        .wence-debug-head-btn-ghost {
          border-color: #e2e8f0 !important;
          background: #ffffff !important;
          color: #475569 !important;
        }
        .wence-debug-page-body {
          flex: 1;
          overflow: auto;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .wence-debug-feedback-banner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid #fecaca;
          background: #fff1f2;
          color: #b91c1c;
          font-size: 12px;
          line-height: 1.5;
        }
        .wence-debug-feedback-banner button {
          border: 1px solid #fecaca;
          border-radius: 8px;
          background: #ffffff;
          color: #b91c1c;
          padding: 4px 10px;
          cursor: pointer;
        }
        .wence-debug-summary-panel {
          border: 1px solid #dbeafe;
          border-radius: 14px;
          background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
          padding: 12px;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
        }
        .wence-debug-summary-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          margin-bottom: 10px;
        }
        .wence-debug-summary-head strong {
          font-size: 14px;
          color: #0f172a;
        }
        .wence-debug-summary-head span {
          font-size: 11px;
          color: #64748b;
        }
        .wence-debug-summary-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
        }
        .wence-debug-summary-item {
          border: 1px solid #dbeafe;
          border-radius: 10px;
          background: #ffffff;
          padding: 10px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .wence-debug-summary-item span {
          font-size: 11px;
          color: #64748b;
        }
        .wence-debug-summary-item strong {
          font-size: 14px;
          color: #0f172a;
          word-break: break-word;
        }
        .wence-debug-summary-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;
        }
        .wence-debug-summary-tag {
          display: inline-flex;
          align-items: center;
          min-height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1e40af;
          font-size: 11px;
          line-height: 1.3;
        }
        .wence-debug-main-entry {
          border: 1px solid #dbeafe;
          border-radius: 14px;
          background: #ffffff;
          padding: 14px 12px;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 8px;
          box-shadow: 0 6px 14px rgba(15, 23, 42, 0.04);
        }
        .wence-debug-main-entry-btn {
          border: 1px solid #93c5fd;
          border-radius: 999px;
          background: linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%);
          color: #1d4ed8;
          padding: 7px 14px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
        }
        .wence-debug-main-entry-hint {
          margin: 0;
          font-size: 12px;
          line-height: 1.55;
          color: #64748b;
        }
        .wence-debug-detail-page {
          position: fixed;
          inset: 0;
          z-index: 1305;
          background: #f8fafc;
          display: flex;
          flex-direction: column;
        }
        .wence-debug-detail-head {
          min-height: 54px;
          border-bottom: 1px solid #e2e8f0;
          background: #ffffff;
          padding: 0 12px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }
        .wence-debug-detail-head strong {
          font-size: 14px;
          color: #0f172a;
        }
        .wence-debug-detail-actions {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .wence-debug-detail-body {
          flex: 1;
          min-height: 0;
          overflow: auto;
          padding: 12px;
        }
        .wence-debug-layout {
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-height: 0;
        }
        .wence-debug-nav {
          display: flex;
          align-items: stretch;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 2px;
          scrollbar-width: thin;
        }
        .wence-debug-nav-btn {
          flex: 0 0 auto;
          border: 1px solid #dbeafe;
          border-radius: 999px;
          background: #ffffff;
          color: #475569;
          padding: 7px 12px;
          font-size: 12px;
          line-height: 1.2;
          cursor: pointer;
          white-space: nowrap;
        }
        .wence-debug-nav-btn.is-active {
          border-color: #93c5fd;
          background: #eff6ff;
          color: #1d4ed8;
          box-shadow: inset 0 0 0 1px rgba(147, 197, 253, 0.25);
        }
        .wence-debug-sections {
          display: flex;
          flex-direction: column;
          gap: 12px;
          min-width: 0;
        }
        .wence-debug-card {
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          background: #ffffff;
          overflow: hidden;
        }
        .wence-debug-section-card {
          display: flex;
          flex-direction: column;
          gap: 10px;
          padding: 12px;
        }
        .wence-debug-card-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
        }
        .wence-debug-card-head-main {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .wence-debug-card-head-main strong {
          font-size: 14px;
          color: #0f172a;
        }
        .wence-debug-card-head-main span {
          font-size: 11px;
          line-height: 1.45;
          color: #64748b;
        }
        .wence-debug-card-actions {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }
        .wence-debug-kv-item {
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 8px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          background: #f8fafc;
        }
        .wence-debug-kv-list {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
        }
        .wence-debug-kv-label {
          font-size: 11px;
          line-height: 1.3;
          color: #64748b;
        }
        .wence-debug-kv-value {
          font-size: 12px;
          line-height: 1.45;
          color: #0f172a;
          word-break: break-word;
        }
        .wence-debug-page-note {
          padding: 10px 12px;
          font-size: 12px;
          color: #334155;
          line-height: 1.55;
        }
        .wence-debug-action-btn {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #ffffff;
          color: #334155;
          padding: 5px 10px;
          font-size: 12px;
          line-height: 1.3;
          cursor: pointer;
        }
        .wence-debug-preview {
          cursor: zoom-in;
        }
        .wence-debug-preview pre {
          margin: 0;
          padding: 12px;
          font-size: 12px;
          line-height: 1.58;
          color: #0f172a;
          white-space: pre-wrap;
          word-break: break-word;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #f8fafc;
          max-height: 240px;
          overflow: auto;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
          user-select: text;
          -webkit-user-select: text;
        }
        .wence-debug-turn-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .wence-debug-turn-card {
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          background: #f8fafc;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .wence-debug-turn-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
        }
        .wence-debug-turn-title-wrap {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .wence-debug-turn-title-wrap strong {
          font-size: 13px;
          color: #0f172a;
        }
        .wence-debug-turn-title-wrap span {
          font-size: 11px;
          color: #64748b;
        }
        .wence-debug-turn-count {
          flex-shrink: 0;
          border-radius: 999px;
          background: #e2e8f0;
          color: #334155;
          padding: 4px 8px;
          font-size: 11px;
          line-height: 1.2;
        }
        .wence-debug-turn-metrics {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .wence-debug-turn-metrics span {
          border-radius: 999px;
          background: #ffffff;
          border: 1px solid #dbeafe;
          color: #1e3a8a;
          padding: 4px 8px;
          font-size: 11px;
          line-height: 1.2;
        }
        .wence-debug-turn-block {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .wence-debug-turn-label {
          font-size: 11px;
          color: #64748b;
        }
        .wence-debug-turn-text {
          margin: 0;
          padding: 10px 11px;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          background: #ffffff;
          font-size: 12px;
          line-height: 1.58;
          color: #0f172a;
          white-space: pre-wrap;
          word-break: break-word;
          user-select: text;
          -webkit-user-select: text;
        }
        .wence-debug-turn-text.is-preview {
          max-height: 112px;
          overflow: auto;
        }
        .wence-debug-turn-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .wence-debug-fullscreen {
          position: fixed;
          inset: 0;
          z-index: 1310;
          background: rgba(2, 6, 23, 0.55);
          display: flex;
          flex-direction: column;
          padding: 18px;
        }
        .wence-debug-fullscreen-head {
          height: 54px;
          flex-shrink: 0;
          background: #ffffff;
          border-radius: 12px 12px 0 0;
          border: 1px solid #e2e8f0;
          border-bottom: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 0 14px;
        }
        .wence-debug-fullscreen-head strong {
          font-size: 14px;
          color: #0f172a;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .wence-debug-fullscreen-actions {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .wence-debug-fullscreen-actions button {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #ffffff;
          color: #334155;
          padding: 4px 10px;
          cursor: pointer;
          font-size: 12px;
        }
        .wence-debug-fullscreen-body {
          flex: 1;
          min-height: 0;
          background: #ffffff;
          border-radius: 0 0 12px 12px;
          border: 1px solid #e2e8f0;
          overflow: auto;
        }
        .wence-debug-fullscreen-body pre {
          margin: 0;
          min-height: 100%;
          padding: 14px;
          font-size: 12px;
          line-height: 1.62;
          color: #0f172a;
          white-space: pre-wrap;
          word-break: break-word;
          user-select: text;
          -webkit-user-select: text;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
        }
        .wence-debug-result-mask {
          position: fixed;
          inset: 0;
          z-index: 1320;
          background: rgba(2, 6, 23, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }
        .wence-debug-result-modal {
          width: min(420px, 100%);
          border-radius: 16px;
          border: 1px solid #dbeafe;
          background: #ffffff;
          box-shadow: 0 18px 34px rgba(15, 23, 42, 0.22);
          padding: 18px;
        }
        .wence-debug-result-modal h3 {
          margin: 0 0 8px 0;
          font-size: 18px;
          color: #0f172a;
        }
        .wence-debug-result-modal p {
          margin: 0;
          font-size: 13px;
          line-height: 1.6;
          color: #475569;
        }
        .wence-debug-result-code {
          margin-top: 14px;
          padding: 14px 16px;
          border-radius: 12px;
          border: 1px dashed #93c5fd;
          background: #eff6ff;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #1d4ed8;
          word-break: break-all;
          text-align: center;
        }
        .wence-debug-result-actions {
          margin-top: 14px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .wence-debug-result-actions button {
          border: 1px solid #d1d5db;
          border-radius: 10px;
          background: #ffffff;
          color: #334155;
          padding: 7px 12px;
          font-size: 13px;
          cursor: pointer;
        }
        .wence-history-head-actions {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .wence-history-item {
          padding: 10px 12px;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 10px;
        }
        .wence-history-main {
          min-width: 0;
          flex: 1;
        }
        .wence-history-main strong {
          display: block;
          font-size: 13px;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .wence-history-main p {
          margin: 0;
          color: #334155;
          font-size: 12px;
          line-height: 1.5;
          word-break: break-word;
        }
        .wence-history-main span {
          display: block;
          margin-top: 6px;
          font-size: 11px;
          color: #64748b;
        }
        .wence-history-actions {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .wence-history-actions button {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          background: #ffffff;
          color: #334155;
          padding: 4px 8px;
          cursor: pointer;
        }
        .wence-history-actions button.danger {
          color: #b91c1c;
          border-color: #fecaca;
          padding: 4px 6px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        @media (max-width: 860px) {
          .wence-debug-page-head {
            min-height: 52px;
            padding: 8px 10px;
            align-items: flex-start;
          }
          .wence-debug-page-head strong {
            font-size: 13px;
            line-height: 1.25;
          }
          .wence-debug-head-actions {
            flex-wrap: wrap;
            justify-content: flex-end;
            gap: 4px;
          }
          .wence-debug-head-btn {
            min-height: 26px;
            padding: 4px 9px;
            font-size: 11px;
          }
          .wence-debug-summary-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .wence-debug-detail-body {
            padding: 10px;
          }
        }
        @media (min-width: 1024px) {
          .wence-debug-page-head {
            min-height: 60px;
            padding: 0 18px;
          }
          .wence-debug-summary-grid {
            grid-template-columns: repeat(6, minmax(0, 1fr));
          }
          .wence-debug-kv-list {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .wence-debug-layout {
            display: grid;
            grid-template-columns: 220px minmax(0, 1fr);
            gap: 12px;
          }
          .wence-debug-nav {
            position: sticky;
            top: 0;
            flex-direction: column;
            align-items: stretch;
            overflow: visible;
            max-height: calc(100vh - 220px);
          }
          .wence-debug-nav-btn {
            width: 100%;
            justify-content: flex-start;
            border-radius: 12px;
            text-align: left;
          }
        }

        .wence-typing-wrap { display: flex; flex-direction: column; align-items: flex-start; gap: 6px; }
        .wence-typing-hint { font-size: 12px; color: #888; }
        .wence-typing-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 24px;
          padding: 0 4px;
        }

        .wence-dot {
          width: 6px;
          height: 6px;
          background-color: #999;
          border-radius: 50%;
          margin: 0 3px;
          animation: bounce 1.4s infinite ease-in-out both;
        }

        .wence-dot:nth-child(1) { animation-delay: -0.32s; }
        .wence-dot:nth-child(2) { animation-delay: -0.16s; }

        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes spinOnce {
          from { transform: rotate(0deg); }
          to { transform: rotate(180deg); }
        }

        .wence-bottom-bar {
          flex: 0 0 auto;
          width: 100%;
          padding: 10px 20px calc(20px + env(safe-area-inset-bottom, 0px)) 20px;
          background: linear-gradient(to top, #fff 80%, rgba(255, 255, 255, 0.8));
          z-index: 10;
          border-top: 1px solid rgba(240, 240, 240, 0.9);
        }

        .wence-input-wrapper {
          display: flex;
          align-items: center;
          background-color: #fff;
          border: 1px solid #eee;
          border-radius: 30px;
          padding: 8px 15px;
          box-shadow: 0 0 15px rgba(230, 220, 255, 0.5);
        }

        .wence-voice-btn {
          width: 28px;
          height: 28px;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 50%;
          border: 1.5px solid #333;
          margin-right: 12px;
          cursor: pointer;
          background: none;
        }

        .wence-chat-input {
          flex: 1;
          border: none;
          outline: none;
          font-size: 15px;
          color: #333;
          background: transparent;
        }

        .wence-chat-input::placeholder {
          color: #ccc;
        }

        .wence-send-btn {
          width: 32px;
          height: 32px;
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: #e8e8e8;
          border-radius: 50%;
          cursor: pointer;
          margin-left: 10px;
          transition: background-color 0.2s;
          border: none;
        }

        .wence-send-btn.active {
          background-color: #333;
        }

        .wence-send-btn:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }

        .wence-send-btn svg {
          color: #fff;
          transform: rotate(-45deg);
        }
        .wence-core-modal-mask {
          position: fixed;
          inset: 0;
          z-index: 1320;
          background: rgba(2, 6, 23, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 18px;
        }
        .wence-core-modal {
          width: min(520px, 100%);
          background: #ffffff;
          border-radius: 14px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 12px 30px rgba(2, 6, 23, 0.18);
          padding: 14px;
        }
        .wence-core-modal h3 {
          margin: 0 0 6px 0;
          font-size: 16px;
          font-weight: 700;
          color: #0f172a;
        }
        .wence-core-modal p {
          margin: 0 0 10px 0;
          font-size: 12px;
          color: #475569;
          line-height: 1.55;
        }
        .wence-core-modal textarea {
          width: 100%;
          min-height: 94px;
          max-height: 260px;
          resize: vertical;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          font-size: 14px;
          color: #0f172a;
          padding: 10px 11px;
          line-height: 1.55;
          outline: none;
        }
        .wence-core-modal textarea:focus {
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
        }
        .wence-core-modal-actions {
          margin-top: 12px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
        }
        .wence-core-modal-actions button {
          border-radius: 9px;
          border: 1px solid #d1d5db;
          background: #ffffff;
          color: #334155;
          padding: 6px 12px;
          cursor: pointer;
          font-size: 13px;
          line-height: 1.3;
        }
        .wence-core-modal-actions button.primary {
          border-color: #dc2626;
          background: #ef4444;
          color: #ffffff;
        }
        .wence-core-modal-actions button:disabled {
          opacity: 0.58;
          cursor: not-allowed;
        }
      `}</style>

      <div className="wence-header">
        <button className="wence-back-btn" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <div className="wence-title">{headerTitle}</div>
        <div className="wence-header-actions">
          <button
            className="wence-icon-btn"
            onClick={() => setHistoryPageOpen(true)}
            aria-label="查看历史对话"
            title="查看历史对话"
          >
            <History size={16} />
          </button>
          <button className="wence-icon-btn" onClick={handleResetConversation} aria-label="开启新聊天" title="开启新聊天">
            <MessageCirclePlus size={16} />
          </button>
        </div>
      </div>

      <div className="wence-main-content">
        {isFirstMessage && messages.length === 0 && (
          <div className="wence-initial-state">
            <div className="wence-profile-section">
              <div className="wence-avatar-container">
                <img src={DAO_AVATAR_SRC} alt={daoName} />
              </div>
              <div className="wence-disclaimer">以下内容均由AI生成，请理性看待</div>
              <div className="wence-greeting">{`${daoName}在此。你先说说现在最困扰你的问题。`}</div>
            </div>

            <div className="wence-suggestions-section">
              <div className="wence-suggestions-header">
                <span>很多人问（可刷新）</span>
                <button
                  className={`wence-refresh-btn ${isRefreshingSuggestions ? 'is-spinning' : ''}`}
                  onClick={handleRefreshSuggestions}
                  aria-label="刷新问题"
                >
                  <RefreshCw size={16} />
                </button>
              </div>
              {suggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion}-${index}`}
                  className="wence-suggestion-btn"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                  <ChevronRight size={18} className="text-gray-300" />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="wence-chat-state">
          {messages.map((message) => {
            const panRecord = message.panRecordId
              ? loadPanRecords().find((r) => r.id === message.panRecordId)
              : undefined;
            const shouldRenderPan = Boolean(
              message.role === 'assistant'
              && panRecord
              && (message.id.startsWith('assistant-pan-preview-') || message.id.startsWith('pan-bridge-')),
            );
            return (
              <div key={message.id} className={`wence-message-row ${message.role === 'user' ? 'wence-user' : 'wence-bot'}`}>
                {message.role === 'assistant' && (
                  <img src={DAO_AVATAR_SRC} alt={daoName} className="wence-chat-avatar" />
                )}
                <div className="wence-message-content">
                  <div className={`wence-bubble ${shouldRenderPan ? 'wence-bubble-pan' : ''}`}>
                    {message.status === 'streaming' && !message.content ? (
                      <div className="wence-typing-wrap">
                        <div className="wence-typing-indicator">
                          <div className="wence-dot" />
                          <div className="wence-dot" />
                          <div className="wence-dot" />
                        </div>
                        {panSummary && bootstrappedPanRecordId !== usedPanRecordId && (
                          <div className="wence-typing-hint">正在思考中，约需 15～45 秒</div>
                        )}
                      </div>
                    ) : (
                      <>
                        {shouldRenderPan && panRecord && <WencePanGrid record={panRecord} />}
                        {message.content ? <div className={shouldRenderPan ? 'wence-bubble-text' : ''}>{message.content}</div> : null}
                      </>
                    )}
                  </div>
                  <div className="wence-message-meta">
                    <div className="wence-message-time">{formatTime(message.timestamp)}</div>
                  </div>
                </div>
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>
        {shouldShowPanCta && !isTyping && (
          <div className="wence-pan-cta-inline">
            <button className="wence-pan-btn wence-pan-btn-inline" onClick={openCoreQuestionModal} disabled={isPanGenerating}>
              <Compass size={14} />
              {isPanGenerating ? '起局中...' : '开始起局'}
            </button>
          </div>
        )}
      </div>

      {debugPageOpen && (
        <WenceDebugPage
          appLabel={appLabel}
          debug={latestDebug}
          history={debugHistory}
          onClose={() => {
            setDebugPageOpen(false);
            setClientLogSubmitResult(null);
          }}
          onSubmitClientLog={handleSubmitCurrentDebugLog}
          clientLogSubmitting={clientLogSubmitting}
          clientLogSubmitResult={clientLogSubmitResult}
          onDismissClientLogResult={dismissClientLogResult}
        />
      )}
      {historyPageOpen && (
        <WenceHistoryPage
          appLabel={appLabel}
          items={historyItems}
          onClose={() => setHistoryPageOpen(false)}
          onOpen={restoreConversationFromHistory}
          onDelete={deleteHistoryItem}
          onClear={clearAllHistory}
        />
      )}
      {coreQuestionModalOpen && (
        <div className="wence-core-modal-mask" role="dialog" aria-modal="true">
          <div className="wence-core-modal">
            <h3>确认核心问题后再起局</h3>
            <p>请用一句话写清你最想问的核心问题。</p>
            <textarea
              ref={coreQuestionInputRef}
              value={coreQuestionDraft}
              onChange={(e) => setCoreQuestionDraft(e.target.value)}
              placeholder="例如：这段关系还有没有继续推进的空间？"
            />
            <div className="wence-core-modal-actions">
              <button type="button" onClick={() => setCoreQuestionModalOpen(false)} disabled={isPanGenerating}>取消</button>
              <button
                type="button"
                className="primary"
                onClick={() => { void handleConfirmCoreQuestionAndGoPan(); }}
                disabled={isPanGenerating || !coreQuestionDraft.trim()}
              >
                {isPanGenerating ? '起局中...' : '开始排盘'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="wence-bottom-bar">
        <div className="wence-input-wrapper">
          <button
            className="wence-voice-btn"
            onClick={handleVoiceButtonTap}
            aria-label="语音输入，双击进入内部调试"
            title="双击进入内部调试"
          >
            <Mic size={14} />
          </button>
          <input
            ref={inputRef}
            type="text"
            className="wence-chat-input"
            placeholder="写下你心里的思绪"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                e.preventDefault();
                void handleSend();
              }
            }}
          />
          <button
            className={`wence-send-btn ${inputValue.trim() ? 'active' : ''}`}
            onClick={() => { void handleSend(); }}
            disabled={!inputValue.trim() || isTyping}
            aria-label="发送消息"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WenceApp;
